// src/provider/roochProvider.tsx
import { createContext as createContext3, useRef } from "react";

// src/provider/clientProvider.tsx
import { createContext as createContext2, useMemo as useMemo2, useState } from "react";
import {
  getRoochNodeUrl,
  RoochClient as RoochClient2
} from "@roochnetwork/rooch-sdk";

// src/provider/useDefaultClient.ts
import { useCallback, useMemo } from "react";
import {
  ErrorValidateInvalidAccountAuthKey,
  ErrorValidateSessionIsExpired,
  isRoochClient,
  RoochClient
} from "@roochnetwork/rooch-sdk";

// src/hooks/useSessionsStore.ts
import { useContext } from "react";
import { useStore } from "zustand";
function useSessionStore(selector) {
  const store = useContext(RoochContext);
  if (!store) {
    throw new Error(
      "Could not find RoochSessionContext. Ensure that you have set up the RoochClientProvider."
    );
  }
  return useStore(store, selector);
}

// src/http/httpTransport.ts
import {
  RoochHTTPTransport
} from "@roochnetwork/rooch-sdk";
var HTTPTransport = class extends RoochHTTPTransport {
  constructor(options, requestErrorCallback) {
    super(options);
    this.requestCallback = requestErrorCallback;
  }
  async request(input) {
    let result;
    try {
      if (input.method === "rooch_executeRawTransaction") {
        this.requestCallback("requesting");
      }
      result = await super.request(input);
      if (input.method === "rooch_executeRawTransaction") {
        this.requestCallback("success");
      }
      return result;
    } catch (e) {
      if ("code" in e) {
        this.requestCallback("error", {
          code: e.code,
          message: e.message
        });
      }
      throw e;
    }
  }
};

// src/provider/globalProvider.tsx
import { createContext, useContext as useContext2 } from "react";
import { jsx } from "react/jsx-runtime";
var RequestEventManager = class {
  constructor() {
    this.callbacks = [];
  }
  subscribe(callback) {
    this.callbacks.push(callback);
    return () => {
      this.callbacks = this.callbacks.filter((cb) => cb !== callback);
    };
  }
  trigger(status) {
    this.callbacks.forEach((callback) => callback(status));
  }
};
var ErrorEventManager = class {
  constructor() {
    this.callbacks = [];
  }
  subscribe(callback) {
    this.callbacks.push(callback);
    return () => {
      this.callbacks = this.callbacks.filter((cb) => cb !== callback);
    };
  }
  trigger(error) {
    this.callbacks.forEach((callback) => callback(error));
  }
};
var GlobalContext = createContext(null);
var GlobalProvider = ({ children }) => {
  const errorEventManager = new ErrorEventManager();
  const requestEventManager = new RequestEventManager();
  const triggerError = (error) => {
    errorEventManager.trigger(error);
  };
  const subscribeOnError = (callback) => {
    return errorEventManager.subscribe(callback);
  };
  const triggerRequest = (status) => {
    requestEventManager.trigger(status);
  };
  const subscribeOnRequest = (callback) => {
    return requestEventManager.subscribe(callback);
  };
  return /* @__PURE__ */ jsx(
    GlobalContext.Provider,
    {
      value: { triggerError, subscribeOnError, triggerRequest, subscribeOnRequest },
      children
    }
  );
};
var useSubscribeOnError = () => {
  const ctx = useContext2(GlobalContext);
  if (!ctx) {
    throw new Error("useSubscribeToError must be used within a GlobalProvider");
  }
  return ctx.subscribeOnError;
};
var useTriggerError = () => {
  const ctx = useContext2(GlobalContext);
  if (!ctx) {
    throw new Error("useTriggerError must be used within a GlobalProvider");
  }
  return ctx.triggerError;
};
var useSubscribeOnRequest = () => {
  const ctx = useContext2(GlobalContext);
  if (!ctx) {
    throw new Error("useSubscribeToError must be used within a GlobalProvider");
  }
  return ctx.subscribeOnRequest;
};
var useTriggerRequest = () => {
  const ctx = useContext2(GlobalContext);
  if (!ctx) {
    throw new Error("useTriggerError must be used within a GlobalProvider");
  }
  return ctx.triggerRequest;
};

// src/provider/useDefaultClient.ts
var DEFAULT_CREATE_CLIENT = (_name, config, requestErrorCallback) => {
  if (isRoochClient(config)) {
    return config;
  }
  config.transport = new HTTPTransport(
    {
      url: config.url.toString()
    },
    requestErrorCallback
  );
  return new RoochClient(config);
};
function useDefaultClient(params) {
  const { currentNetwork, networks } = params;
  const currentSession = useSessionStore((state) => state.currentSession);
  const removeSession = useSessionStore((state) => state.removeSession);
  const triggerError = useTriggerError();
  const triggerRequest = useTriggerRequest();
  const _requestErrorCallback = useCallback(
    (state, error) => {
      try {
        if (state === "error") {
          if (error.code === ErrorValidateInvalidAccountAuthKey || error.code === ErrorValidateSessionIsExpired) {
            if (currentSession) {
              removeSession(currentSession);
            }
          }
          triggerError(error);
        }
        triggerRequest(state);
      } catch (e) {
        console.error(e);
      }
    },
    [triggerError, currentSession, removeSession, triggerRequest]
  );
  return useMemo(() => {
    return DEFAULT_CREATE_CLIENT(currentNetwork, networks[currentNetwork], _requestErrorCallback);
  }, [currentNetwork, networks, _requestErrorCallback]);
}

// src/provider/clientProvider.tsx
import { jsx as jsx2 } from "react/jsx-runtime";
var ClientContext = createContext2(null);
var DEFAULT_NETWORKS = {
  localnet: { url: getRoochNodeUrl("localnet") }
};
function RoochClientProvider(props) {
  const { onNetworkChange, network, children } = props;
  const networks = props.networks ?? DEFAULT_NETWORKS;
  const [selectedNetwork, setSelectedNetwork] = useState(
    props.network ?? props.defaultNetwork ?? Object.keys(networks)[0]
  );
  const currentNetwork = props.network ?? selectedNetwork;
  const client = useDefaultClient({ currentNetwork, networks });
  const ctx = useMemo2(() => {
    return {
      client,
      network: currentNetwork,
      networks,
      config: networks[currentNetwork] instanceof RoochClient2 ? null : networks[currentNetwork],
      selectNetwork: (newNetwork) => {
        if (currentNetwork === newNetwork) {
          return;
        }
        if (!network && newNetwork !== selectedNetwork) {
          setSelectedNetwork(newNetwork);
        }
        onNetworkChange?.(newNetwork);
      }
    };
  }, [client, currentNetwork, networks, network, selectedNetwork, onNetworkChange]);
  return /* @__PURE__ */ jsx2(ClientContext.Provider, { value: ctx, children });
}

// src/provider/sessionStore.ts
import { createStore } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { Session } from "@roochnetwork/rooch-sdk";
function createSessionStore({ storage, storageKey, sessionConf }) {
  return createStore()(
    persist(
      (set, get2) => ({
        sessions: [],
        currentSession: null,
        sessionConf,
        addSession(session) {
          const cache = get2().sessions;
          cache.push(session);
          set(() => ({
            sessions: cache
          }));
        },
        setCurrentSession(session) {
          if (!session) {
            set(() => ({
              currentSession: null
            }));
          } else {
            const cache = get2().sessions;
            if (!cache.find((item) => item.getAuthKey() === session.getAuthKey())) {
              cache.push(session);
            }
            set(() => ({
              currentSession: session,
              sessions: cache
            }));
          }
        },
        removeSession(session) {
          const cacheSessions = get2().sessions;
          const cacheCurSession = get2().currentSession;
          set(() => ({
            currentSession: cacheCurSession?.getAuthKey() === session.getAuthKey() ? null : cacheCurSession,
            sessions: cacheSessions.filter((c) => c.getAuthKey() !== session.getAuthKey())
          }));
        }
      }),
      {
        name: storageKey,
        storage: createJSONStorage(() => storage, {
          reviver: (key, value) => {
            if (key === "sessions") {
              return value.map((session) => Session.fromJson(session));
            }
            return value;
          }
        }),
        partialize: ({ sessions }) => ({
          sessions
        })
      }
    )
  );
}

// src/utils/stateStorage.ts
function createInMemoryStore() {
  const store = /* @__PURE__ */ new Map();
  return {
    getItem(key) {
      return store.get(key);
    },
    setItem(key, value) {
      store.set(key, value);
    },
    removeItem(key) {
      store.delete(key);
    }
  };
}
function getDefaultStorage(type) {
  let storage;
  switch (type) {
    case 0 /* Session */:
      storage = typeof window !== "undefined" && window.sessionStorage ? sessionStorage : void 0;
      break;
    case 1 /* Local */:
      storage = typeof window !== "undefined" && window.localStorage ? localStorage : void 0;
  }
  if (!storage) {
    storage = createInMemoryStore();
  }
  return storage;
}

// src/wellet/wallet.ts
import { Signer } from "@roochnetwork/rooch-sdk";
var Wallet = class extends Signer {
  /**
   * Checks if the wallet is installed.
   * @returns A promise that resolves to true if the wallet is installed, otherwise false.
   */
  async checkInstalled() {
    for (let i = 1; i < 10 && !this.getTarget(); i += 1) {
      await new Promise((resolve) => setTimeout(resolve, 100 * i));
    }
    return Promise.resolve(this.getTarget() !== void 0);
  }
};

// src/wellet/bitcoin.ts
import {
  Authenticator,
  BitcoinSignMessage,
  Secp256k1PublicKey,
  fromHEX
} from "@roochnetwork/rooch-sdk";
var BitcoinWallet = class extends Wallet {
  async signTransaction(input) {
    const message = new BitcoinSignMessage(input.hashData(), input.getInfo() || "");
    return Authenticator.bitcoin(message, this, "raw");
  }
  getPublicKey() {
    if (!this.publicKey) {
      throw Error("Please connect your wallet first");
    }
    return new Secp256k1PublicKey(fromHEX(this.publicKey));
  }
  getRoochAddress() {
    if (!this.currentAddress) {
      throw Error("Please connect your wallet first");
    }
    return this.currentAddress.genRoochAddress();
  }
  getBitcoinAddress() {
    if (!this.currentAddress) {
      throw Error("Please connect your wallet first");
    }
    return this.currentAddress;
  }
  getKeyScheme() {
    return "Secp256k1";
  }
  normalize_recovery_id(v) {
    let normalizeV = v - 27 - 4;
    if (normalizeV < 0) {
      normalizeV = normalizeV + 4;
    }
    return normalizeV;
  }
  switchAccount() {
    throw new Error("Method not implemented.");
  }
  getChain() {
    return "bitcoin";
  }
};

// src/wellet/unisat.ts
import { BitcoinAddress as BitcoinAddress2, str, bytes } from "@roochnetwork/rooch-sdk";

// src/wellet/types.ts
var All_NETWORK = ["testnet", "livenet"];

// src/wellet/unisat.ts
var UniSatWallet = class extends BitcoinWallet {
  getName() {
    return "UniSat";
  }
  getIcon(_) {
    return "data:image/svg+xml;base64,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";
  }
  getDescription() {
    return "UniSat Wallet";
  }
  getInstallUrl() {
    return "";
  }
  getTarget() {
    return window.unisat;
  }
  async connect() {
    let addresses = await this.getTarget().getAccounts();
    if (!addresses || addresses.length === 0) {
      await this.getTarget().requestAccounts();
      return this.connect();
    }
    let publicKey = await this.getTarget().getPublicKey();
    this.address = addresses.map((item) => new BitcoinAddress2(item));
    this.currentAddress = this.address[0];
    this.publicKey = publicKey;
    return this.address;
  }
  async switchNetwork(network) {
    return this.getTarget().switchNetwork(network);
  }
  getNetwork() {
    return this.getTarget().getNetwork();
  }
  getSupportNetworks() {
    return All_NETWORK;
  }
  onAccountsChanged(callback) {
    this.getTarget().on("accountsChanged", callback);
  }
  removeAccountsChanged(callback) {
    this.getTarget().removeListener("accountsChanged", callback);
  }
  onNetworkChanged(callback) {
    this.getTarget().on("networkChanged", callback);
  }
  removeNetworkChanged(callback) {
    this.getTarget().removeListener("networkChanged", callback);
  }
  async sign(msg) {
    const msgStr = str("utf8", msg);
    const sign = await this.getTarget().signMessage(msgStr);
    return bytes("base64", sign).subarray(1);
  }
  sendBtc(input) {
    return this.getTarget().sendBitcoin(input.toAddress, input.satoshis, input.options);
  }
  getBalance() {
    return this.getTarget().getBalance();
  }
};

// src/wellet/okx.ts
import { BitcoinAddress as BitcoinAddress3, str as str2, bytes as bytes2 } from "@roochnetwork/rooch-sdk";
var OkxWallet = class extends BitcoinWallet {
  getName() {
    return "OKX";
  }
  getIcon(_) {
    return "data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz48c3ZnIGlkPSJhIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxNTAgMTUwIj48ZGVmcz48c3R5bGU+LmV7ZmlsbDpub25lO308L3N0eWxlPjwvZGVmcz48ZyBpZD0iYiI+PGcgaWQ9ImMiPjxwYXRoIGlkPSJkIiBjbGFzcz0iZSIgZD0iTTAsMEgxNTBWMTUwSDBWMFoiLz48L2c+PC9nPjxwYXRoIGQ9Ik0xMy44MSwxMy41N3YxMjMuOThoMTIzLjk4VjEzLjU3SDEzLjgxWm0yNi44MiwyOC42NGMwLS44NywuNzEtMS41OCwxLjU4LTEuNThoMjAuM2MuODcsMCwxLjU4LC43MSwxLjU4LDEuNTh2MjAuM2MwLC44OC0uNzEsMS41OS0xLjU4LDEuNTloLTIwLjNjLS44NywwLTEuNTgtLjcxLTEuNTgtMS41OXYtMjAuM1ptMjMuNDYsNjYuN2MwLC44Ny0uNzEsMS41OC0xLjU4LDEuNThoLTIwLjNjLS44NywwLTEuNTgtLjcxLTEuNTgtMS41OHYtMjAuM2MwLS44OCwuNzEtMS41OSwxLjU4LTEuNTloMjAuM2MuODcsMCwxLjU4LC43MSwxLjU4LDEuNTl2MjAuM1ptMjEuODYtMjEuNjJoLTIwLjNjLS44NywwLTEuNTktLjcxLTEuNTktMS41OXYtMjAuM2MwLS44NywuNzEtMS41OSwxLjU5LTEuNTloMjAuM2MuODcsMCwxLjU5LC43MSwxLjU5LDEuNTl2MjAuM2MwLC44Ny0uNzEsMS41OS0xLjU5LDEuNTlabTI1LjA1LDIxLjYyYzAsLjg3LS43MSwxLjU4LTEuNTksMS41OGgtMjAuM2MtLjg3LDAtMS41OC0uNzEtMS41OC0xLjU4di0yMC4zYzAtLjg4LC43MS0xLjU5LDEuNTgtMS41OWgyMC4zYy44NywwLDEuNTksLjcxLDEuNTksMS41OXYyMC4zWm0wLTQ2LjQxYzAsLjg4LS43MSwxLjU5LTEuNTksMS41OWgtMjAuM2MtLjg3LDAtMS41OC0uNzEtMS41OC0xLjU5di0yMC4zYzAtLjg3LC43MS0xLjU4LDEuNTgtMS41OGgyMC4zYy44NywwLDEuNTksLjcxLDEuNTksMS41OHYyMC4zWiIvPjwvc3ZnPg==";
  }
  getDescription() {
    return "OKX Wallet";
  }
  getInstallUrl() {
    return "https://chromewebstore.google.com/detail/okx-wallet/mcohilncbfahbmgdjkbpemcciiolgcge";
  }
  async sign(msg) {
    const msgStr = str2("utf8", msg);
    const sign = await this.getTarget().signMessage(msgStr, {
      from: this.currentAddress?.toStr()
    });
    return bytes2("base64", sign).subarray(1);
  }
  getTarget() {
    return window.okxwallet?.bitcoin;
  }
  // BUG: If the wallet is locked, no reply will be received
  // TODO: Use timeout fix ?
  async connect() {
    const timeoutPromise = new Promise(
      (_, reject) => setTimeout(() => reject(new Error("Connection timed out")), 2e4)
    );
    const obj = await Promise.race([this.getTarget().connect(), timeoutPromise]);
    this.currentAddress = new BitcoinAddress3(obj.address);
    this.publicKey = obj.compressedPublicKey !== "" ? obj.compressedPublicKey : obj.publicKey;
    this.address = [this.currentAddress];
    return this.address;
  }
  switchNetwork(_) {
    throw Error("okx not support switch network!");
  }
  getNetwork() {
    return this.getTarget().getNetwork();
  }
  getSupportNetworks() {
    return ["livenet"];
  }
  onAccountsChanged(callback) {
    this.getTarget().on("accountsChanged", callback);
  }
  removeAccountsChanged(callback) {
    this.getTarget().removeListener("accountsChanged", callback);
  }
  onNetworkChanged(callback) {
    this.getTarget().on("networkChanged", callback);
  }
  removeNetworkChanged(callback) {
    this.getTarget().removeListener("networkChanged", callback);
  }
  // TODO: okx not support test btc
  sendBtc(input) {
    return this.getTarget().sendBitcoin(input.toAddress, input.satoshis, input.options);
  }
  getBalance() {
    return this.getTarget().getBalance();
  }
};

// src/wellet/onekey.ts
import { BitcoinAddress as BitcoinAddress4, str as str3, bytes as bytes3 } from "@roochnetwork/rooch-sdk";
var OnekeyWallet = class extends BitcoinWallet {
  getName() {
    return "OneKey";
  }
  getIcon(theme) {
    if (theme === "dark") {
      return "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTQ0IiBoZWlnaHQ9IjE0NCIgdmlld0JveD0iMCAwIDE0NCAxNDQiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxwYXRoIGZpbGwtcnVsZT0iZXZlbm9kZCIgY2xpcC1ydWxlPSJldmVub2RkIiBkPSJNNzIgMTQ0QzEyMS43MDYgMTQ0IDE0NCAxMjEuNzA2IDE0NCA3MkMxNDQgMjIuMjk0NCAxMjEuNzA2IDAgNzIgMEMyMi4yOTQ0IDAgMCAyMi4yOTQ0IDAgNzJDMCAxMjEuNzA2IDIyLjI5NDQgMTQ0IDcyIDE0NFpNNTguNDc1MyAzMC41MzA1SDc4LjUwNTNWNjMuNTM4MUg2Ni4wODY1VjQxLjE1NjJINTQuOTYxM0w1OC40NzUzIDMwLjUzMDVaTTcyLjAwMDQgMTEzLjQ2OUM4NC42MTY0IDExMy40NjkgOTQuODQzNyAxMDMuMjQyIDk0Ljg0MzcgOTAuNjI2MUM5NC44NDM3IDc4LjAxMDEgODQuNjE2NCA2Ny43ODI4IDcyLjAwMDQgNjcuNzgyOEM1OS4zODQ0IDY3Ljc4MjggNDkuMTU3MSA3OC4wMTAxIDQ5LjE1NzEgOTAuNjI2MUM0OS4xNTcxIDEwMy4yNDIgNTkuMzg0NCAxMTMuNDY5IDcyLjAwMDQgMTEzLjQ2OVpNNzIuMDAwNCAxMDMuMDk5Qzc4Ljg4ODkgMTAzLjA5OSA4NC40NzMxIDk3LjUxNDUgODQuNDczMSA5MC42MjZDODQuNDczMSA4My43Mzc1IDc4Ljg4ODkgNzguMTUzMyA3Mi4wMDA0IDc4LjE1MzNDNjUuMTExOSA3OC4xNTMzIDU5LjUyNzYgODMuNzM3NSA1OS41Mjc2IDkwLjYyNkM1OS41Mjc2IDk3LjUxNDUgNjUuMTExOSAxMDMuMDk5IDcyLjAwMDQgMTAzLjA5OVoiIGZpbGw9ImJsYWNrIiBzdHlsZT0iZmlsbDpibGFjaztmaWxsLW9wYWNpdHk6MTsiLz4KPC9zdmc+Cg==";
    }
    return "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTQ0IiBoZWlnaHQ9IjE0NCIgdmlld0JveD0iMCAwIDE0NCAxNDQiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxwYXRoIGZpbGwtcnVsZT0iZXZlbm9kZCIgY2xpcC1ydWxlPSJldmVub2RkIiBkPSJNNzIgMTQ0QzEyMS43MDYgMTQ0IDE0NCAxMjEuNzA2IDE0NCA3MkMxNDQgMjIuMjk0NCAxMjEuNzA2IDAgNzIgMEMyMi4yOTQ0IDAgMCAyMi4yOTQ0IDAgNzJDMCAxMjEuNzA2IDIyLjI5NDQgMTQ0IDcyIDE0NFpNNTguNDc1MyAzMC41MzA1SDc4LjUwNTNWNjMuNTM4MUg2Ni4wODY1VjQxLjE1NjJINTQuOTYxM0w1OC40NzUzIDMwLjUzMDVaTTcyLjAwMDQgMTEzLjQ2OUM4NC42MTY0IDExMy40NjkgOTQuODQzNyAxMDMuMjQyIDk0Ljg0MzcgOTAuNjI2MUM5NC44NDM3IDc4LjAxMDEgODQuNjE2NCA2Ny43ODI4IDcyLjAwMDQgNjcuNzgyOEM1OS4zODQ0IDY3Ljc4MjggNDkuMTU3MSA3OC4wMTAxIDQ5LjE1NzEgOTAuNjI2MUM0OS4xNTcxIDEwMy4yNDIgNTkuMzg0NCAxMTMuNDY5IDcyLjAwMDQgMTEzLjQ2OVpNNzIuMDAwNCAxMDMuMDk5Qzc4Ljg4ODkgMTAzLjA5OSA4NC40NzMxIDk3LjUxNDUgODQuNDczMSA5MC42MjZDODQuNDczMSA4My43Mzc1IDc4Ljg4ODkgNzguMTUzMyA3Mi4wMDA0IDc4LjE1MzNDNjUuMTExOSA3OC4xNTMzIDU5LjUyNzYgODMuNzM3NSA1OS41Mjc2IDkwLjYyNkM1OS41Mjc2IDk3LjUxNDUgNjUuMTExOSAxMDMuMDk5IDcyLjAwMDQgMTAzLjA5OVoiIGZpbGw9IiM0NEQ2MkMiIHN0eWxlPSJmaWxsOiM0NEQ2MkM7ZmlsbDpjb2xvcihkaXNwbGF5LXAzIDAuMjY2NyAwLjgzOTIgMC4xNzI1KTtmaWxsLW9wYWNpdHk6MTsiLz4KPC9zdmc+Cg==";
  }
  getDescription() {
    return "OneKey Wallet";
  }
  getInstallUrl() {
    return "https://chromewebstore.google.com/detail/onekey/jnmbobjmhlngoefaiojfljckilhhlhcj";
  }
  async sign(msg) {
    const msgStr = str3("utf8", msg);
    const sign = await this.getTarget().signMessage(msgStr);
    return bytes3("base64", sign).subarray(1);
  }
  getTarget() {
    return window.$onekey?.btc;
  }
  async connect() {
    let addresses = await this.getTarget().getAccounts();
    if (!addresses || addresses.length === 0) {
      await this.getTarget().requestAccounts();
      return this.connect();
    }
    let publicKey = await this.getTarget().getPublicKey();
    this.address = addresses.map((item) => new BitcoinAddress4(item));
    this.currentAddress = this.address[0];
    this.publicKey = publicKey;
    return this.address;
  }
  // TODO: onekey provider switch api, But it doesn't work.
  switchNetwork(_) {
    throw Error("onekey not support switch network!");
  }
  getNetwork() {
    return this.getTarget().getNetwork();
  }
  getSupportNetworks() {
    return All_NETWORK;
  }
  onAccountsChanged(callback) {
    this.getTarget().on("accountsChanged", callback);
  }
  removeAccountsChanged(callback) {
    this.getTarget().removeListener("accountsChanged", callback);
  }
  onNetworkChanged(callback) {
    this.getTarget().on("networkChanged", callback);
  }
  removeNetworkChanged(callback) {
    this.getTarget().removeListener("networkChanged", callback);
  }
  sendBtc(input) {
    return this.getTarget().sendBitcoin(input.toAddress, input.satoshis, input.options);
  }
  getBalance() {
    return this.getTarget().getBalance();
  }
};

// src/wellet/error.ts
var USER_REJECTED = 4001;
var NOT_SUPPORT_ADDRESS = 99991;
var AMOUNT_INVALID = 99999;
var WalletError = class extends Error {
  constructor(code, msg) {
    super(msg);
    this.code = code;
  }
};

// src/utils/walletUtils.ts
var unisatWallet = new UniSatWallet();
var okxWallet = new OkxWallet();
var onekeyWallet = new OnekeyWallet();
var supportedWallets = [unisatWallet, okxWallet, onekeyWallet];
async function checkWallets(filter) {
  const wallets2 = supportedWallets.filter(
    (wallet) => wallet.getChain() === filter || !filter
  );
  return await Promise.all(wallets2.filter(async (w) => await w.checkInstalled()));
}

// src/components/styling/InjectedThemeStyles.tsx
import { assignInlineVars } from "@vanilla-extract/dynamic";

// src/constants/styleDataAttribute.ts
var styleDataAttributeName = "data-sdk-kit";
var styleDataAttributeSelector = `[${styleDataAttributeName}]`;
var styleDataAttribute = { [styleDataAttributeName]: "" };

// src/themes/themeContract.ts
import { createGlobalThemeContract } from "@vanilla-extract/css";
var themeContractValues = {
  blurs: {
    modalOverlay: ""
  },
  backgroundColors: {
    primaryButton: "",
    primaryButtonHover: "",
    outlineButtonHover: "",
    walletItemHover: "",
    walletItemSelected: "",
    modalOverlay: "",
    modalPrimary: "",
    modalSecondary: "",
    iconButton: "",
    iconButtonHover: "",
    dropdownMenu: "",
    dropdownMenuSeparator: ""
  },
  borderColors: {
    outlineButton: ""
  },
  colors: {
    primaryButton: "",
    outlineButton: "",
    body: "",
    bodyMuted: "",
    bodyDanger: "",
    bodyWarning: "",
    iconButton: ""
  },
  radii: {
    small: "",
    medium: "",
    large: "",
    xlarge: ""
  },
  shadows: {
    primaryButton: "",
    walletItemSelected: ""
  },
  fontWeights: {
    normal: "",
    medium: "",
    bold: ""
  },
  fontSizes: {
    small: "",
    medium: "",
    large: "",
    xlarge: ""
  },
  typography: {
    fontFamily: "",
    fontStyle: "",
    lineHeight: "",
    letterSpacing: ""
  }
};
var themeVars = createGlobalThemeContract(
  themeContractValues,
  (_, path) => `dapp-kit-${path.join("-")}`
);

// src/components/styling/InjectedThemeStyles.tsx
import { jsx as jsx3 } from "react/jsx-runtime";
function InjectedThemeStyles({ theme }) {
  const themeStyles = Array.isArray(theme) ? getDynamicThemeStyles(theme) : getStaticThemeStyles(theme);
  return /* @__PURE__ */ jsx3(
    "style",
    {
      precedence: "default",
      href: "rooch-sdk-kit-theme",
      dangerouslySetInnerHTML: {
        __html: themeStyles
      }
    }
  );
}
function getDynamicThemeStyles(themes) {
  return themes.map(({ mediaQuery, selector, variables }) => {
    const themeStyles = getStaticThemeStyles(variables);
    const themeStylesWithSelectorPrefix = selector ? `${selector} ${themeStyles}` : themeStyles;
    return mediaQuery ? `@media ${mediaQuery}{${themeStylesWithSelectorPrefix}}` : themeStylesWithSelectorPrefix;
  }).join(" ");
}
function getStaticThemeStyles(theme) {
  return `${styleDataAttributeSelector} {${cssStringFromTheme(theme)}}`;
}
function cssStringFromTheme(theme) {
  return Object.entries(assignInlineVars(themeVars, theme)).map(([key, value]) => `${key}:${value};`).join("");
}

// src/themes/lightTheme.ts
var lightTheme = {
  blurs: {
    modalOverlay: "blur(0)"
  },
  backgroundColors: {
    primaryButton: "#F6F7F9",
    primaryButtonHover: "#F0F2F5",
    outlineButtonHover: "#F4F4F5",
    modalOverlay: "rgba(24 36 53 / 20%)",
    modalPrimary: "white",
    modalSecondary: "#F7F8F8",
    iconButton: "transparent",
    iconButtonHover: "#F0F1F2",
    dropdownMenu: "#FFFFFF",
    dropdownMenuSeparator: "#F3F6F8",
    walletItemSelected: "white",
    walletItemHover: "#3C424226"
  },
  borderColors: {
    outlineButton: "#E4E4E7"
  },
  colors: {
    primaryButton: "#373737",
    outlineButton: "#373737",
    iconButton: "#000000",
    body: "#182435",
    bodyMuted: "#767A81",
    bodyDanger: "#FF794B",
    bodyWarning: "#FF4C4C"
  },
  radii: {
    small: "6px",
    medium: "8px",
    large: "12px",
    xlarge: "16px"
  },
  shadows: {
    primaryButton: "0px 4px 12px rgba(0, 0, 0, 0.1)",
    walletItemSelected: "0px 2px 6px rgba(0, 0, 0, 0.05)"
  },
  fontWeights: {
    normal: "400",
    medium: "500",
    bold: "600"
  },
  fontSizes: {
    small: "14px",
    medium: "16px",
    large: "18px",
    xlarge: "20px"
  },
  typography: {
    fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
    fontStyle: "normal",
    lineHeight: "1.3",
    letterSpacing: "1"
  }
};

// src/provider/roochProvider.tsx
import { jsx as jsx4, jsxs } from "react/jsx-runtime";
var DEFAULT_SESSION_STORAGE_KEY = function(_) {
  return "rooch-sdk-kit:rooch-session-info";
};
var RoochContext = createContext3(null);
function RoochProvider(props) {
  const { children, networks, defaultNetwork, theme, sessionConf } = props;
  const storeRef = useRef(
    createSessionStore({
      storage: getDefaultStorage(1 /* Local */),
      storageKey: DEFAULT_SESSION_STORAGE_KEY(),
      sessionConf
    })
  );
  return /* @__PURE__ */ jsx4(RoochContext.Provider, { value: storeRef.current, children: /* @__PURE__ */ jsx4(GlobalProvider, { children: /* @__PURE__ */ jsxs(RoochClientProvider, { networks, defaultNetwork, children: [
    /* @__PURE__ */ jsx4(InjectedThemeStyles, { theme: theme ?? lightTheme }),
    children
  ] }) }) });
}

// src/provider/walletProvider.tsx
import { useCallback as useCallback2, createContext as createContext4, useEffect as useEffect2, useRef as useRef2 } from "react";
import { BitcoinAddress as BitcoinAddress5 } from "@roochnetwork/rooch-sdk";

// src/provider/walletStore.ts
import { createStore as createStore2 } from "zustand";
import { createJSONStorage as createJSONStorage2, persist as persist2 } from "zustand/middleware";
function createWalletStore({
  chain,
  currentWallet,
  wallets: wallets2,
  storage,
  storageKey,
  autoConnectEnabled
}) {
  return createStore2()(
    persist2(
      (set, get2) => ({
        currentChain: chain,
        autoConnectEnabled,
        currentWallet,
        wallets: wallets2,
        addresses: [],
        currentAddress: void 0,
        lastConnectedAddress: void 0,
        lastConnectedWalletName: void 0,
        connectionStatus: "disconnected",
        setChain(chain2) {
          const currentChain = get2().currentChain;
          if (currentChain === chain2) {
            return;
          }
          set(() => ({
            currentChain: chain2,
            accounts: [],
            // currentWallet: supportWallets.find((v) => v.getSupportNetworks()),
            sessionAccount: null,
            connectionStatus: "disconnected"
          }));
        },
        setConnectionStatus(connectionStatus3) {
          set(() => ({
            connectionStatus: connectionStatus3
          }));
        },
        setWalletConnected(wallet, connectedAddresses, selectedAddress) {
          set(() => ({
            currentWallet: wallet,
            accounts: connectedAddresses,
            currentAddress: selectedAddress || void 0,
            lastConnectedWalletName: wallet.getName(),
            lastConnectedAddress: selectedAddress?.toStr(),
            connectionStatus: "connected"
          }));
        },
        setWalletDisconnected() {
          set(() => ({
            accounts: [],
            currentAddress: void 0,
            lastConnectedWalletName: void 0,
            lastConnectedAddress: void 0,
            connectionStatus: "disconnected"
          }));
        },
        setAddressSwitched(selected) {
          set(() => ({
            currentAddress: selected,
            lastConnectedAddress: selected.toStr() ?? ""
          }));
        },
        updateWalletAddresses(addresses) {
          const currentAddr = get2().currentAddress;
          set(() => ({
            currentAddress: currentAddr && addresses.find((addr) => addr.toStr() === currentAddr.toStr()) || addresses[0]
          }));
        },
        updateWallets(wallets3) {
          set(() => ({
            wallets: wallets3
          }));
        }
      }),
      {
        name: storageKey,
        storage: createJSONStorage2(() => storage),
        partialize: ({ lastConnectedWalletName, lastConnectedAddress }) => ({
          lastConnectedWalletName,
          lastConnectedAddress
        })
      }
    )
  );
}

// src/hooks/client/useCurrentNetwork.ts
function useCurrentNetwork() {
  return useRoochContext().network;
}

// src/hooks/client/useRoochContext.ts
import { useContext as useContext3 } from "react";
function useRoochContext() {
  const context = useContext3(ClientContext);
  if (!context) {
    throw new Error(
      "Could not find RoochClientContext. Ensure that you have set up the RoochClientProvider."
    );
  }
  return context;
}

// src/hooks/client/useRoochClient.ts
function useRoochClient() {
  return useRoochContext().client;
}

// src/hooks/client/useRoochClientQuery.ts
import { useQuery } from "@tanstack/react-query";
function useRoochClientQuery(...args) {
  const [method, params, { queryKey = [], ...options } = {}] = args;
  const network = useCurrentNetwork();
  const roochClient = useRoochClient();
  return useQuery({
    ...options,
    queryKey: [network, method, params, ...queryKey],
    queryFn: async () => {
      return await roochClient[method](params);
    }
  });
}

// src/hooks/client/useSwitchNetwork.ts
import { useMutation } from "@tanstack/react-query";

// src/constants/roochMutationKeys.ts
function formMutationKeyFn(baseEntity) {
  return function mutationKeyFn(additionalKeys = []) {
    return [{ ...roochMutationKeys.all, baseEntity }, ...additionalKeys];
  };
}
var roochMutationKeys = {
  all: { baseScope: "rooch" },
  addNetwork: formMutationKeyFn("add-network"),
  switchNetwork: formMutationKeyFn("switch-network"),
  removeNetwork: formMutationKeyFn("remove-network"),
  removeSession: formMutationKeyFn("remove-session"),
  transferObject: formMutationKeyFn("transfer-object"),
  transferCoin: formMutationKeyFn("transfer-coin"),
  signAndExecuteTransaction: formMutationKeyFn("sign-and-execute-transaction")
};

// src/constants/walletMutationKeys.ts
function formMutationKeyFn2(baseEntity) {
  return function mutationKeyFn(additionalKeys = []) {
    return [{ ...walletMutationKeys.all, baseEntity }, ...additionalKeys];
  };
}
var walletMutationKeys = {
  all: { baseScope: "wallet" },
  connectWallet: formMutationKeyFn2("connect-wallet"),
  autoConnectWallet: formMutationKeyFn2("auto-connect-wallet"),
  switchAccount: formMutationKeyFn2("switch-account"),
  createSessionKey: formMutationKeyFn2("create-session-key")
};

// src/hooks/client/useSwitchNetwork.ts
function useSwitchNetwork({
  mutationKey,
  ...mutationOptions
} = {}) {
  const switchNetwork = useRoochContext().selectNetwork;
  return useMutation({
    mutationKey: roochMutationKeys.switchNetwork(mutationKey),
    mutationFn: async (args) => {
      switchNetwork(args);
    },
    ...mutationOptions
  });
}

// src/hooks/client/useTransferObject.ts
import { useMutation as useMutation2 } from "@tanstack/react-query";
function useTransferObject({
  mutationKey,
  ...mutationOptions
} = {}) {
  const client = useRoochClient();
  return useMutation2({
    mutationKey: roochMutationKeys.transferObject(mutationKey),
    mutationFn: async (args) => {
      const result = await client.transferObject(args);
      if (result.execution_info.status.type !== "executed") {
        Error("transfer failed" + result.execution_info.status.type);
      }
    },
    ...mutationOptions
  });
}

// src/hooks/client/useTransferCoin.ts
import { useMutation as useMutation3 } from "@tanstack/react-query";

// src/hooks/useCurrentSession.ts
function useCurrentSession() {
  return useSessionStore((state) => state.currentSession);
}

// src/hooks/client/useTransferCoin.ts
function useTransferCoin({
  mutationKey,
  ...mutationOptions
} = {}) {
  const client = useRoochClient();
  const curSession = useCurrentSession();
  return useMutation3({
    mutationKey: roochMutationKeys.transferCoin(mutationKey),
    mutationFn: async (args) => {
      const signer = args.signer || curSession;
      if (signer === null) {
        throw Error("");
      }
      const result = await client.transfer({
        ...args,
        signer: args.signer || curSession
      });
      if (result.execution_info.status.type !== "executed") {
        Error("transfer failed" + result.execution_info.status.type);
      }
    },
    ...mutationOptions
  });
}

// src/hooks/client/networks-config.ts
function createNetworkConfig(networkConfig) {
  function useNetworkConfig() {
    const { config } = useRoochContext();
    if (!config) {
      throw new Error("No network config found");
    }
    return config;
  }
  function useNetworkVariables() {
    const { variables } = useNetworkConfig();
    return variables ?? {};
  }
  function useNetworkVariable(name) {
    const variables = useNetworkVariables();
    return variables[name];
  }
  return {
    networkConfig,
    useNetworkConfig,
    useNetworkVariables,
    useNetworkVariable
  };
}

// src/hooks/client/useSignAndExecuteTransaction.ts
import { useMutation as useMutation4 } from "@tanstack/react-query";
function useSignAndExecuteTransaction({
  mutationKey,
  ...mutationOptions
} = {}) {
  const client = useRoochClient();
  const session = useCurrentSession();
  return useMutation4({
    mutationKey: roochMutationKeys.signAndExecuteTransaction(mutationKey),
    mutationFn: async (args) => {
      if (!session) {
        throw Error("Create a session first");
      }
      const result = await client.signAndExecuteTransaction({
        transaction: args.transaction,
        signer: args.signer || session
      });
      if (result.execution_info.status.type !== "executed" && result.execution_info.status) {
        Error("transfer failed" + result.execution_info.status.type);
      }
      return result;
    },
    ...mutationOptions
  });
}

// src/hooks/wallet/useWalletStore.ts
import { useContext as useContext4 } from "react";
import { useStore as useStore2 } from "zustand";
function useWalletStore(selector) {
  const store = useContext4(WalletContext);
  if (!store) {
    throw new Error("Could not find WalletContext. Ensure that you have set up the WalletProvider.");
  }
  return useStore2(store, selector);
}

// src/hooks/wallet/useAddresses.ts
function useAddresses() {
  return useWalletStore((state) => state.addresses);
}

// src/hooks/wallet/useAutoConnectWallet.ts
import { useQuery as useQuery2 } from "@tanstack/react-query";
import { useLayoutEffect, useState as useState2 } from "react";
function useAutoConnectWallet() {
  const { mutateAsync: connectWallet } = useConnectWallet();
  const autoConnectEnabled = useWalletStore((state) => state.autoConnectEnabled);
  const lastConnectedWalletName = useWalletStore((state) => state.lastConnectedWalletName);
  const lastConnectedAddress = useWalletStore((state) => state.lastConnectedAddress);
  const { isConnected } = useCurrentWallet();
  const wallets2 = useWallets();
  const [clientOnly, setClientOnly] = useState2(false);
  useLayoutEffect(() => {
    setClientOnly(true);
  }, []);
  const { data, isError } = useQuery2({
    queryKey: [
      "@rooch/sdk-kit",
      "autoconnect",
      {
        isConnected,
        autoConnectEnabled,
        lastConnectedWalletName,
        lastConnectedAddress
      }
    ],
    queryFn: async () => {
      if (!autoConnectEnabled) {
        return "disabled";
      }
      if (!lastConnectedWalletName || !lastConnectedAddress || isConnected) {
        return "attempted";
      }
      let wallet = wallets2.find((wallet2) => wallet2.getName() === lastConnectedWalletName);
      if (wallet) {
        await connectWallet({ wallet });
      }
      return "attempted";
    },
    enabled: autoConnectEnabled,
    persister: void 0,
    gcTime: 0,
    staleTime: 0,
    networkMode: "always",
    retry: (_) => {
      return false;
    },
    retryOnMount: false,
    refetchInterval: 1e3,
    refetchIntervalInBackground: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false
  });
  if (!autoConnectEnabled) {
    return "disabled";
  }
  if (!clientOnly) {
    return "idle";
  }
  if (!lastConnectedWalletName) {
    return "attempted";
  }
  return isError ? "attempted" : data ?? "idle";
}

// src/hooks/wallet/useConnectWallet.ts
import { useMutation as useMutation5 } from "@tanstack/react-query";

// src/hooks/useSessions.ts
function useSessions() {
  return useSessionStore(
    (state) => state.sessions.sort((a, b) => b.getCreateTime() - a.getCreateTime())
  );
}

// src/hooks/wallet/useConnectWallet.ts
function useConnectWallet({
  mutationKey,
  ...mutationOptions
} = {}) {
  const sessions = useSessions();
  const setCurrentSession = useSessionStore((state) => state.setCurrentSession);
  const setWalletConnected = useWalletStore((state) => state.setWalletConnected);
  const setWalletDisconnected = useWalletStore((state) => state.setWalletDisconnected);
  const setConnectionStatus = useWalletStore((state) => state.setConnectionStatus);
  const triggerError = useTriggerError();
  return useMutation5({
    mutationKey: walletMutationKeys.connectWallet(mutationKey),
    mutationFn: async ({ wallet }) => {
      try {
        setConnectionStatus("connecting");
        const connectAddress = await wallet.connect();
        const selectedAddress = connectAddress[0];
        setWalletConnected(wallet, connectAddress, selectedAddress);
        const cur = sessions.find(
          (item) => item.getRoochAddress().toStr() === selectedAddress?.genRoochAddress().toStr()
        );
        setCurrentSession(cur);
        return connectAddress;
      } catch (error) {
        setWalletDisconnected();
        if ("code" in error && "message" in error) {
          triggerError(error);
        }
        throw error;
      }
    },
    ...mutationOptions
  });
}

// src/hooks/wallet/useCurrentAddress.ts
function useCurrentAddress() {
  return useWalletStore((state) => state.currentAddress);
}

// src/hooks/wallet/useCurrentWallet.ts
function useCurrentWallet() {
  const currentWallet = useWalletStore((state) => state.currentWallet);
  const connectionStatus3 = useWalletStore((state) => state.connectionStatus);
  switch (connectionStatus3) {
    case "connecting":
      return {
        status: connectionStatus3,
        wallet: currentWallet,
        isDisconnected: false,
        isConnecting: true,
        isConnected: false
      };
    case "disconnected":
      return {
        status: connectionStatus3,
        wallet: currentWallet,
        isDisconnected: true,
        isConnecting: false,
        isConnected: false
      };
    case "connected": {
      return {
        status: connectionStatus3,
        wallet: currentWallet,
        isDisconnected: false,
        isConnecting: false,
        isConnected: true
      };
    }
  }
}

// src/hooks/wallet/useWallets.ts
function useWallets() {
  return useWalletStore((state) => state.wallets);
}

// src/hooks/wallet/useWalletChanged.ts
import { useEffect } from "react";

// src/wellet/wallets.ts
var wallets = void 0;
var registered = /* @__PURE__ */ new Set();
var listeners = {};
function getWallets() {
  if (wallets)
    return wallets;
  wallets = Object.freeze({ register, get, on });
  return wallets;
}
function register(...wallets2) {
  wallets2.forEach((wallet) => registered.add(wallet));
  listeners["register"]?.forEach((listener) => guard(() => listener(...wallets2)));
  return function unregister() {
    wallets2.forEach((wallet) => registered.delete(wallet));
    listeners["unregister"]?.forEach((listener) => guard(() => listener(...wallets2)));
  };
}
function get() {
  return [...registered];
}
function on(event, listener) {
  listeners[event]?.push(listener) || (listeners[event] = [listener]);
  return function off() {
    listeners[event] = listeners[event]?.filter((existingListener) => listener !== existingListener);
  };
}
function guard(callback) {
  try {
    callback();
  } catch (error) {
    console.error(error);
  }
}

// src/wellet/util.ts
function getRegisteredWallets(preferredWallets, walletFilter) {
  const walletsApi = getWallets();
  const wallets2 = walletsApi.get();
  const Wallets = wallets2.filter((wallet) => !walletFilter || walletFilter(wallet));
  return [
    // Preferred wallets, in order:
    ...preferredWallets.map((name) => Wallets.find((wallet) => wallet.getName() === name)),
    // Wallets in default order:
    ...Wallets.filter((wallet) => !preferredWallets.includes(wallet.getName()))
  ].filter((wallet) => wallet !== void 0);
}

// src/hooks/wallet/useWalletChanged.ts
function useWalletChanged(preferredWallets, walletFilter) {
  const updateWallets = useWalletStore((state) => state.updateWallets);
  useEffect(() => {
    const api = getWallets();
    updateWallets(getRegisteredWallets(preferredWallets, walletFilter));
    const unsubscribeFromRegister = api.on("register", () => {
      updateWallets(getRegisteredWallets(preferredWallets, walletFilter));
    });
    return () => {
      unsubscribeFromRegister();
    };
  }, [preferredWallets, updateWallets, walletFilter]);
}

// src/hooks/wallet/useConnectionStatus.ts
function useConnectionStatus() {
  return useWalletStore((state) => state.connectionStatus);
}

// src/hooks/useSession.ts
function useSession(scope) {
  return useSessionStore(
    (state) => state.sessions.find(
      (item) => scope.includes("::") ? item.getScopes().find((_scope) => _scope === scope) : item.getScopes().find((_scope) => _scope.startsWith(scope)) !== void 0
    )
  );
}

// src/hooks/useRemoveSession.ts
import { useMutation as useMutation6 } from "@tanstack/react-query";
function useRemoveSession({
  mutationKey,
  ...mutationOptions
} = {}) {
  const sessionsKeys = useSessions();
  const removeSession = useSessionStore((state) => state.removeSession);
  const setCurrentSession = useSessionStore((state) => state.setCurrentSession);
  const currentSession = useCurrentSession();
  const client = useRoochClient();
  return useMutation6({
    mutationKey: roochMutationKeys.removeSession(mutationKey),
    mutationFn: async (args) => {
      try {
        if (!currentSession) {
          return;
        }
        const result = await client.removeSession({
          authKey: args.authKey,
          signer: currentSession
        });
        if (result) {
          let cacheSession = sessionsKeys.find(
            (item) => item.getAuthKey() === args.authKey
          );
          if (cacheSession) {
            removeSession(cacheSession);
            if (cacheSession.getAuthKey() === currentSession?.getAuthKey()) {
              const substitute = sessionsKeys.filter((item) => item.getAuthKey() !== cacheSession.getAuthKey()).sort((a, b) => b.getCreateTime() - a.getCreateTime()).filter((item) => !item.isSessionExpired());
              if (substitute.length > 0) {
                setCurrentSession(substitute[0]);
              } else {
                setCurrentSession(void 0);
              }
            }
          }
        }
      } catch (e) {
        throw e;
      }
    },
    ...mutationOptions
  });
}

// src/hooks/useCreateSession.ts
import { useMutation as useMutation7 } from "@tanstack/react-query";

// src/error/walletErrors.ts
var WalletNotConnectedError = class extends Error {
};

// src/hooks/useCreateSession.ts
function useCreateSessionKey({
  mutationKey,
  ...mutationOptions
} = {}) {
  const client = useRoochClient();
  const { wallet } = useCurrentWallet();
  const setCurrentSession = useSessionStore((state) => state.setCurrentSession);
  const triggerError = useTriggerError();
  return useMutation7({
    mutationKey: walletMutationKeys.createSessionKey(mutationKey),
    mutationFn: async (args) => {
      const signer = args.signer || wallet;
      if (!signer) {
        throw new WalletNotConnectedError("No wallet is connected.");
      }
      try {
        const sessionAccount = await client.createSession({
          signer,
          sessionArgs: args
        });
        setCurrentSession(sessionAccount);
        return sessionAccount;
      } catch (error) {
        if ("code" in error && "message" in error) {
          triggerError(error);
        }
        throw error;
      }
    },
    ...mutationOptions
  });
}

// src/provider/walletProvider.tsx
import { jsx as jsx5 } from "react/jsx-runtime";
var DEFAULT_STORAGE_KEY = "rooch-sdk-kit:wallet-connect-info";
var WalletContext = createContext4(null);
function WalletProvider({
  preferredWallets = ["UniSat", "OKX"],
  chain = "bitcoin",
  storage,
  storageKey = DEFAULT_STORAGE_KEY,
  autoConnect = false,
  children
}) {
  const network = useCurrentNetwork();
  const storeRef = useRef2(
    createWalletStore({
      chain,
      wallets: getRegisteredWallets(preferredWallets, (w) => w.getChain() === chain),
      currentWallet: void 0,
      autoConnectEnabled: autoConnect,
      storage: storage || getDefaultStorage(1 /* Local */),
      storageKey: storageKey + network + chain?.toString()
    })
  );
  useEffect2(() => {
    const fetchWallet = async () => {
      const wallets2 = await checkWallets(chain);
      getWallets().register(...wallets2);
    };
    fetchWallet();
  }, [chain]);
  return /* @__PURE__ */ jsx5(WalletContext.Provider, { value: storeRef.current, children: /* @__PURE__ */ jsx5(WalletConnectionManager, { preferredWallets, chain, children }) });
}
function WalletConnectionManager({ children, preferredWallets }) {
  useAutoConnectWallet();
  useWalletChanged(preferredWallets);
  const connectionStatus3 = useWalletStore((store) => store.connectionStatus);
  const currentWallet = useWalletStore((store) => store.currentWallet);
  const setWalletDisconnected = useWalletStore((store) => store.setWalletDisconnected);
  const setConnectionStatus = useWalletStore((state) => state.setConnectionStatus);
  const setAddressSwitched = useWalletStore((store) => store.setAddressSwitched);
  const currentAddress = useWalletStore((state) => state.currentAddress);
  const sessions = useSessions();
  const curSession = useCurrentSession();
  const setCurrentSession = useSessionStore((state) => state.setCurrentSession);
  const accountsChangedHandler = useCallback2(
    async (address) => {
      if (address.length === 0) {
        setWalletDisconnected();
      } else {
        setConnectionStatus("connecting");
        const selectedAddress = address[0];
        if (selectedAddress !== currentAddress?.toStr()) {
          setAddressSwitched(new BitcoinAddress5(selectedAddress));
          setCurrentSession(void 0);
          const cur = sessions.find(
            (item) => item.getRoochAddress().toStr() === currentAddress?.genRoochAddress().toStr()
          );
          if (cur && cur.getAuthKey() !== curSession?.getAuthKey()) {
            setCurrentSession(cur);
          }
        }
      }
    },
    [
      sessions,
      curSession,
      currentAddress,
      setAddressSwitched,
      setConnectionStatus,
      setCurrentSession,
      setWalletDisconnected
    ]
  );
  useEffect2(() => {
    if (connectionStatus3 === "connected") {
      currentWallet?.onAccountsChanged(accountsChangedHandler);
    }
    return () => {
      if (connectionStatus3 === "connected") {
        currentWallet?.removeAccountsChanged(accountsChangedHandler);
      }
    };
  }, [accountsChangedHandler, connectionStatus3, currentWallet]);
  return children;
}

// src/components/connect-modal/ConnectModal.tsx
import * as Dialog2 from "@radix-ui/react-dialog";
import clsx6 from "clsx";
import { useEffect as useEffect3, useState as useState6 } from "react";

// src/components/icons/BackIcon.tsx
import { jsx as jsx6 } from "react/jsx-runtime";
function BackIcon(props) {
  return /* @__PURE__ */ jsx6("svg", { width: 24, height: 24, fill: "none", xmlns: "http://www.w3.org/2000/svg", ...props, children: /* @__PURE__ */ jsx6(
    "path",
    {
      d: "M7.57 12.262c0 .341.13.629.403.895l5.175 5.059c.204.205.45.307.751.307.609 0 1.101-.485 1.101-1.087 0-.293-.123-.574-.349-.8L10.14 12.27l4.511-4.375A1.13 1.13 0 0 0 15 7.087C15 6.485 14.508 6 13.9 6c-.295 0-.54.103-.752.308l-5.175 5.058c-.28.28-.404.56-.404.896Z",
      fill: "currentColor"
    }
  ) });
}

// src/components/ui/Heading.tsx
import { Slot } from "@radix-ui/react-slot";
import clsx from "clsx";
import { forwardRef } from "react";

// src/components/ui/Heading.css.ts
import { createRuntimeFn as _7a468 } from "@vanilla-extract/recipes/createRuntimeFn";
var headingVariants = _7a468({ defaultClassName: "Heading__1v58k1t0", variantClassNames: { size: { sm: "Heading_headingVariants_size_sm__1v58k1t1", md: "Heading_headingVariants_size_md__1v58k1t2", lg: "Heading_headingVariants_size_lg__1v58k1t3", xl: "Heading_headingVariants_size_xl__1v58k1t4" }, weight: { normal: "Heading_headingVariants_weight_normal__1v58k1t5", bold: "Heading_headingVariants_weight_bold__1v58k1t6" }, truncate: { true: "Heading_headingVariants_truncate_true__1v58k1t7" } }, defaultVariants: { size: "lg", weight: "bold" }, compoundVariants: [] });

// src/components/ui/Heading.tsx
import { jsx as jsx7 } from "react/jsx-runtime";
var Heading = forwardRef(
  ({
    children,
    className,
    asChild = false,
    as: Tag = "h1",
    size,
    weight,
    truncate,
    ...headingProps
  }, forwardedRef) => {
    return /* @__PURE__ */ jsx7(
      Slot,
      {
        ...headingProps,
        ref: forwardedRef,
        className: clsx(headingVariants({ size, weight, truncate }), className),
        children: asChild ? children : /* @__PURE__ */ jsx7(Tag, { children })
      }
    );
  }
);
Heading.displayName = "Heading";

// src/components/ui/IconButton.tsx
import { Slot as Slot2 } from "@radix-ui/react-slot";
import clsx2 from "clsx";
import { forwardRef as forwardRef2 } from "react";

// src/components/ui/IconButton.css.ts
var container = "IconButton_container__5zr4fg0";

// src/components/ui/IconButton.tsx
import { jsx as jsx8 } from "react/jsx-runtime";
var IconButton = forwardRef2(
  ({ className, asChild = false, ...props }, forwardedRef) => {
    const Comp = asChild ? Slot2 : "button";
    return /* @__PURE__ */ jsx8(Comp, { ...props, className: clsx2(container, className), ref: forwardedRef });
  }
);
IconButton.displayName = "Button";

// src/components/connect-modal/ConnectModal.css.ts
var backButtonContainer = "ConnectModal_backButtonContainer__yocf846";
var selectedViewContainer = "ConnectModal_selectedViewContainer__yocf845";
var title = "ConnectModal_title__yocf841";
var viewContainer = "ConnectModal_viewContainer__yocf844";
var walletListContainer = "ConnectModal_walletListContainer__yocf849";
var walletListContainerWithViewSelected = "ConnectModal_walletListContainerWithViewSelected__yocf84a";
var walletListContent = "ConnectModal_walletListContent__yocf848";
var whatIsAWalletButton = "ConnectModal_whatIsAWalletButton__yocf843";

// src/components/ui/Button.tsx
import { Slot as Slot3 } from "@radix-ui/react-slot";
import clsx3 from "clsx";
import { forwardRef as forwardRef3 } from "react";

// src/components/ui/Button.css.ts
import { createRuntimeFn as _7a4682 } from "@vanilla-extract/recipes/createRuntimeFn";
var buttonVariants = _7a4682({ defaultClassName: "Button_buttonVariants__1vdhm2g0", variantClassNames: { variant: { primary: "Button_buttonVariants_variant_primary__1vdhm2g1", outline: "Button_buttonVariants_variant_outline__1vdhm2g2" }, size: { md: "Button_buttonVariants_size_md__1vdhm2g3", lg: "Button_buttonVariants_size_lg__1vdhm2g4" } }, defaultVariants: { variant: "primary", size: "md" }, compoundVariants: [] });

// src/components/ui/Button.tsx
import { jsx as jsx9 } from "react/jsx-runtime";
var Button = forwardRef3(
  ({ className, variant, size, asChild = false, ...props }, forwardedRef) => {
    const Comp = asChild ? Slot3 : "button";
    return /* @__PURE__ */ jsx9(
      Comp,
      {
        ...props,
        className: clsx3(buttonVariants({ variant, size }), className),
        ref: forwardedRef
      }
    );
  }
);
Button.displayName = "Button";

// src/components/ui/Text.tsx
import { Slot as Slot4 } from "@radix-ui/react-slot";
import clsx4 from "clsx";
import { forwardRef as forwardRef4 } from "react";

// src/components/ui/Text.css.ts
import { createRuntimeFn as _7a4683 } from "@vanilla-extract/recipes/createRuntimeFn";
var textVariants = _7a4683({ defaultClassName: "Text__1utg4wj0", variantClassNames: { size: { sm: "Text_textVariants_size_sm__1utg4wj1" }, weight: { normal: "Text_textVariants_weight_normal__1utg4wj2", medium: "Text_textVariants_weight_medium__1utg4wj3", bold: "Text_textVariants_weight_bold__1utg4wj4" }, color: { muted: "Text_textVariants_color_muted__1utg4wj5", danger: "Text_textVariants_color_danger__1utg4wj6", warning: "Text_textVariants_color_warning__1utg4wj7" }, mono: { true: "Text_textVariants_mono_true__1utg4wj8" } }, defaultVariants: { size: "sm", weight: "normal" }, compoundVariants: [] });

// src/components/ui/Text.tsx
import { jsx as jsx10 } from "react/jsx-runtime";
var Text = forwardRef4(
  ({
    children,
    className,
    asChild = false,
    as: Tag = "div",
    size,
    weight,
    color,
    mono,
    ...textProps
  }, forwardedRef) => {
    return /* @__PURE__ */ jsx10(
      Slot4,
      {
        ...textProps,
        ref: forwardedRef,
        className: clsx4(textVariants({ size, weight, color, mono }), className),
        children: asChild ? children : /* @__PURE__ */ jsx10(Tag, { children })
      }
    );
  }
);
Text.displayName = "Text";

// src/components/connect-modal/views/ConnectionStatus.css.ts
var connectionStatus = "ConnectionStatus_connectionStatus__l246a63";
var container2 = "ConnectionStatus_container__l246a60";
var retryButtonContainer = "ConnectionStatus_retryButtonContainer__l246a64";
var title2 = "ConnectionStatus_title__l246a62";
var walletIcon = "ConnectionStatus_walletIcon__l246a61";

// src/components/connect-modal/views/ConnectionStatus.tsx
import { jsx as jsx11, jsxs as jsxs2 } from "react/jsx-runtime";
function ConnectionStatus({
  selectedWallet,
  info,
  hadConnectionError,
  onRetryConnection
}) {
  return /* @__PURE__ */ jsxs2("div", { className: container2, children: [
    selectedWallet.getName() && /* @__PURE__ */ jsx11(
      "img",
      {
        className: walletIcon,
        src: selectedWallet.getIcon(),
        alt: `${selectedWallet.getName()} logo`
      }
    ),
    /* @__PURE__ */ jsx11("div", { className: title2, children: /* @__PURE__ */ jsxs2(Heading, { as: "h2", size: "xl", children: [
      "Opening ",
      selectedWallet.getName()
    ] }) }),
    /* @__PURE__ */ jsxs2("div", { className: connectionStatus, children: [
      hadConnectionError ? /* @__PURE__ */ jsx11(Text, { color: "danger", children: "Connection failed" }) : /* @__PURE__ */ jsx11(Text, { color: "muted", children: "Confirm connection in the wallet..." }),
      info ? info.map((item, i) => /* @__PURE__ */ jsx11(Text, { color: "warning", children: item }, i)) : null
    ] }),
    hadConnectionError ? /* @__PURE__ */ jsx11("div", { className: retryButtonContainer, children: /* @__PURE__ */ jsx11(Button, { type: "button", variant: "outline", onClick: () => onRetryConnection(selectedWallet), children: "Retry Connection" }) }) : null
  ] });
}

// src/components/connect-modal/InfoSection.css.ts
var container3 = "InfoSection_container__1tyxmhu0";

// src/components/connect-modal/InfoSection.tsx
import { jsx as jsx12, jsxs as jsxs3 } from "react/jsx-runtime";
function InfoSection({ title: title7, children }) {
  return /* @__PURE__ */ jsxs3("section", { className: container3, children: [
    /* @__PURE__ */ jsx12(Heading, { as: "h3", size: "sm", weight: "normal", children: title7 }),
    /* @__PURE__ */ jsx12(Text, { weight: "medium", color: "muted", children })
  ] });
}

// src/components/connect-modal/views/WhatIsAWallet.css.ts
var container4 = "WhatIsAWallet_container__18x371x0";
var content = "WhatIsAWallet_content__18x371x1";

// src/components/connect-modal/views/WhatIsAWallet.tsx
import { jsx as jsx13, jsxs as jsxs4 } from "react/jsx-runtime";
function WhatIsAWallet() {
  return /* @__PURE__ */ jsxs4("div", { className: container4, children: [
    /* @__PURE__ */ jsx13(Heading, { as: "h2", children: "What is a Wallet" }),
    /* @__PURE__ */ jsxs4("div", { className: content, children: [
      /* @__PURE__ */ jsx13(InfoSection, { title: "Easy Login", children: "No need to create new accounts and passwords for every website. Just connect your wallet and get going." }),
      /* @__PURE__ */ jsx13(InfoSection, { title: "Store your Digital Assets", children: "Send, receive, store, and display your digital assets like NFTs & coins." })
    ] })
  ] });
}

// src/components/connect-modal/views/InstallStatus.css.ts
var container5 = "InstallStatus_container__1hlwsco0";
var installButtonContainer = "InstallStatus_installButtonContainer__1hlwsco4";
var installStatus = "InstallStatus_installStatus__1hlwsco3";
var title3 = "InstallStatus_title__1hlwsco2";
var walletIcon2 = "InstallStatus_walletIcon__1hlwsco1";

// src/components/connect-modal/views/InstallStatus.tsx
import { jsx as jsx14, jsxs as jsxs5 } from "react/jsx-runtime";
function InstallStatus({ selectedWallet }) {
  return /* @__PURE__ */ jsxs5("div", { className: container5, children: [
    selectedWallet.getName() && /* @__PURE__ */ jsx14(
      "img",
      {
        className: walletIcon2,
        src: selectedWallet.getIcon(),
        alt: `${selectedWallet.getName()} logo`
      }
    ),
    /* @__PURE__ */ jsx14("div", { className: title3, children: /* @__PURE__ */ jsxs5(Heading, { as: "h2", size: "xl", children: [
      "Opening ",
      selectedWallet.getName()
    ] }) }),
    /* @__PURE__ */ jsx14("div", { className: installStatus, children: /* @__PURE__ */ jsx14(Text, { color: "danger", children: "undetected Wallet" }) }),
    /* @__PURE__ */ jsx14("div", { className: installButtonContainer, children: /* @__PURE__ */ jsx14(
      Button,
      {
        type: "button",
        variant: "outline",
        onClick: () => window.open(selectedWallet.getInstallUrl(), "_blank"),
        children: "Install"
      }
    ) })
  ] });
}

// src/components/connect-modal/wallet-list/WalletList.css.ts
var container6 = "WalletList_container__1wac1dp0";

// src/components/connect-modal/wallet-list/WalletListItem.tsx
import { clsx as clsx5 } from "clsx";

// src/components/connect-modal/wallet-list/WalletListItem.css.ts
var container7 = "WalletListItem_container__1i3e7c00";
var selectedWalletItem = "WalletListItem_selectedWalletItem__1i3e7c02";
var walletIcon3 = "WalletListItem_walletIcon__1i3e7c03";
var walletItem = "WalletListItem_walletItem__1i3e7c01";

// src/components/connect-modal/wallet-list/WalletListItem.tsx
import { jsx as jsx15, jsxs as jsxs6 } from "react/jsx-runtime";
function WalletListItem({ name, icon, onClick, isSelected = false }) {
  return /* @__PURE__ */ jsx15("li", { className: container7, children: /* @__PURE__ */ jsxs6(
    "button",
    {
      className: clsx5(walletItem, { [selectedWalletItem]: isSelected }),
      type: "button",
      onClick,
      children: [
        icon && typeof icon === "string" ? /* @__PURE__ */ jsx15("img", { className: walletIcon3, src: icon, alt: `${name} logo` }) : icon,
        /* @__PURE__ */ jsx15(Heading, { size: "md", truncate: true, asChild: true, children: /* @__PURE__ */ jsx15("div", { children: name }) })
      ]
    }
  ) });
}

// src/components/connect-modal/wallet-list/WalletList.tsx
import { jsx as jsx16 } from "react/jsx-runtime";
function WalletList({ selectedWalletName, onSelect }) {
  const wallets2 = useWallets();
  return /* @__PURE__ */ jsx16("ul", { className: container6, children: wallets2.map((wallet) => /* @__PURE__ */ jsx16(
    WalletListItem,
    {
      name: wallet.getName(),
      icon: wallet.getIcon(),
      isSelected: wallet.getName() === selectedWalletName,
      onClick: () => onSelect(wallet)
    },
    wallet.getName()
  )) });
}

// src/components/ui/Modal.tsx
import { useState as useState4 } from "react";
import * as Dialog from "@radix-ui/react-dialog";

// src/components/ui/Modal.css.ts
var closeButtonContainer = "Modal_closeButtonContainer__1c5z17f2";
var content2 = "Modal_content__1c5z17f1";
var overlay = "Modal_overlay__1c5z17f0";

// src/components/styling/StyleMarker.tsx
import { Slot as Slot5 } from "@radix-ui/react-slot";
import { forwardRef as forwardRef5 } from "react";
import { jsx as jsx17 } from "react/jsx-runtime";
var StyleMarker = forwardRef5(({ children, ...props }, forwardedRef) => /* @__PURE__ */ jsx17(Slot5, { ref: forwardedRef, ...props, ...styleDataAttribute, children }));
StyleMarker.displayName = "StyleMarker";

// src/components/ProgressProvider.tsx
import { createContext as createContext5, useCallback as useCallback3, useContext as useContext5, useState as useState3 } from "react";

// src/components/fauct-modal/views/FaucetView.css.ts
var container8 = "FaucetView_container__1ff62y00";
var content3 = "FaucetView_content__1ff62y01";
var createButtonContainer = "FaucetView_createButtonContainer__1ff62y02";
var progressBar = "FaucetView_progressBar__1ff62y03";

// src/components/ui/Progress.tsx
import { jsx as jsx18 } from "react/jsx-runtime";
function Progress() {
  const { progress } = useProgress();
  return /* @__PURE__ */ jsx18("div", { className: progressBar, style: { width: `${progress}%` } });
}

// src/components/ProgressProvider.tsx
import { jsx as jsx19, jsxs as jsxs7 } from "react/jsx-runtime";
var ProgressProviderContext = createContext5(null);
var ProgressProvider = ({ children }) => {
  const [progress, setProgress] = useState3(0);
  const [loading, setLoading] = useState3(false);
  const start = useCallback3(() => {
    setLoading(true);
    setProgress(0);
    const interval = setInterval(() => {
      setProgress((prev) => {
        const nextProgress = prev + 10;
        if (nextProgress >= 70) {
          clearInterval(interval);
        }
        return Math.min(nextProgress, 70);
      });
    }, 100);
  }, []);
  const finish = useCallback3((callback) => {
    const interval = setInterval(() => {
      setProgress((prev) => {
        const nextProgress = prev + 5;
        if (nextProgress >= 100) {
          clearInterval(interval);
          setTimeout(() => {
            setLoading(false);
            if (callback) {
              callback();
            }
          }, 300);
        }
        return Math.min(nextProgress, 100);
      });
    }, 50);
  }, []);
  return /* @__PURE__ */ jsxs7(ProgressProviderContext.Provider, { value: { loading, progress, start, finish }, children: [
    children,
    loading && /* @__PURE__ */ jsx19(Progress, {})
  ] });
};
var useProgress = () => {
  const ctx = useContext5(ProgressProviderContext);
  if (!ctx) {
    throw new Error("useSubscribeToError must be used within a GlobalProvider");
  }
  return ctx;
};

// src/components/icons/CloseIcon.tsx
import { jsx as jsx20 } from "react/jsx-runtime";
function CloseIcon(props) {
  return /* @__PURE__ */ jsx20("svg", { width: 10, height: 10, fill: "none", xmlns: "http://www.w3.org/2000/svg", ...props, children: /* @__PURE__ */ jsx20(
    "path",
    {
      d: "M9.708.292a.999.999 0 0 0-1.413 0l-3.289 3.29L1.717.291A.999.999 0 0 0 .305 1.705l3.289 3.289-3.29 3.289a.999.999 0 1 0 1.413 1.412l3.29-3.289 3.288 3.29a.999.999 0 0 0 1.413-1.413l-3.29-3.29 3.29-3.288a.999.999 0 0 0 0-1.413Z",
      fill: "currentColor"
    }
  ) });
}

// src/components/ui/Modal.tsx
import { jsx as jsx21, jsxs as jsxs8 } from "react/jsx-runtime";
function Modal({ trigger, children, open, defaultOpen, onOpenChange }) {
  const [isModalOpen, setModalOpen] = useState4(open ?? defaultOpen);
  const handleOpenChange = (open2) => {
    setModalOpen(open2);
    onOpenChange?.(open2);
  };
  return /* @__PURE__ */ jsxs8(Dialog.Root, { open: open ?? isModalOpen, onOpenChange: handleOpenChange, children: [
    /* @__PURE__ */ jsx21(Dialog.Trigger, { asChild: true, children: trigger }),
    /* @__PURE__ */ jsx21(Dialog.Portal, { children: /* @__PURE__ */ jsx21(StyleMarker, { children: /* @__PURE__ */ jsx21(Dialog.Overlay, { className: overlay, children: /* @__PURE__ */ jsxs8(Dialog.Content, { className: content2, "aria-describedby": void 0, children: [
      /* @__PURE__ */ jsx21(ProgressProvider, { children }),
      /* @__PURE__ */ jsx21(Dialog.Close, { className: closeButtonContainer, asChild: true, children: /* @__PURE__ */ jsx21(IconButton, { type: "button", "aria-label": "Close", children: /* @__PURE__ */ jsx21(CloseIcon, {}) }) })
    ] }) }) }) })
  ] });
}

// src/components/switch-network-modal/views/SwitchNetworkView.css.ts
var connectionStatus2 = "SwitchNetworkView_connectionStatus__e4ayqy3";
var container9 = "SwitchNetworkView_container__e4ayqy0";
var retryButtonContainer2 = "SwitchNetworkView_retryButtonContainer__e4ayqy4";
var title4 = "SwitchNetworkView_title__e4ayqy2";
var walletIcon4 = "SwitchNetworkView_walletIcon__e4ayqy1";

// src/components/switch-network-modal/views/SwitchNetworkView.tsx
import { useState as useState5 } from "react";

// src/components/util/wallet.ts
var NETWORK_MAP = {
  mainnet: "livenet",
  testnet: "testnet",
  devnet: void 0,
  localnet: void 0
};
var checkWalletNetwork = async (wallet, roochNetwork) => {
  try {
    const walletNetwork = await wallet.getNetwork();
    const target = NETWORK_MAP[roochNetwork];
    if (target && walletNetwork !== target) {
      return target;
    }
  } catch (_) {
  }
  return void 0;
};

// src/components/switch-network-modal/views/SwitchNetworkView.tsx
import { jsx as jsx22, jsxs as jsxs9 } from "react/jsx-runtime";
function SwitchNetworkView({
  wallet,
  onSuccess,
  targetNetWork,
  switchNetwork
}) {
  const { start, finish, loading } = useProgress();
  const [error, setError] = useState5(false);
  const [support, setSupport] = useState5(true);
  const roochNetwork = useCurrentNetwork();
  const switch2Network = () => {
    start();
    switchNetwork(wallet, targetNetWork).catch((e) => {
      if ("message" in e && e.message.includes("not support")) {
        setSupport(false);
      }
      setError(true);
    }).finally(() => {
      finish();
    });
  };
  const refresh = () => {
    start();
    checkWalletNetwork(wallet, roochNetwork).then((result) => {
      if (!result) {
        onSuccess();
      }
    }).finally(() => finish());
  };
  return /* @__PURE__ */ jsxs9("div", { className: container9, children: [
    wallet.getName() && /* @__PURE__ */ jsx22(
      "img",
      {
        className: walletIcon4,
        src: wallet.getIcon(),
        alt: `${wallet.getName()} logo`
      }
    ),
    /* @__PURE__ */ jsx22("div", { className: title4, children: /* @__PURE__ */ jsxs9(Heading, { as: "h2", size: "xl", children: [
      "Check ",
      wallet.getName()
    ] }) }),
    /* @__PURE__ */ jsxs9("div", { className: connectionStatus2, children: [
      /* @__PURE__ */ jsx22(Text, { color: "danger", children: !loading ? `${error ? "Switch failed" : `Wallet network is not ${targetNetWork}`}` : "being processed..." }),
      /* @__PURE__ */ jsx22(Text, { color: "muted", children: !loading ? `Please ${error ? "manually" : ""} switch the wallet network to ${targetNetWork}` : "" })
    ] }),
    /* @__PURE__ */ jsx22("div", { className: retryButtonContainer2, children: /* @__PURE__ */ jsx22(
      Button,
      {
        disabled: loading,
        type: "button",
        variant: "outline",
        onClick: () => support ? switch2Network() : refresh(),
        children: support ? error ? "Retry Switch" : "Switch" : "Refresh"
      }
    ) })
  ] });
}

// src/components/connect-modal/ConnectModal.tsx
import { jsx as jsx23, jsxs as jsxs10 } from "react/jsx-runtime";
function ConnectModal({
  trigger,
  open,
  defaultOpen,
  onOpenChange,
  onSuccess
}) {
  const wallets2 = useWallets();
  const { mutateAsync, isError } = useConnectWallet();
  const roochNetwork = useCurrentNetwork();
  const [isModalOpen, setModalOpen] = useState6(open ?? defaultOpen);
  const [currentView, setCurrentView] = useState6();
  const [targetNetwork, setTargetNetwork] = useState6();
  const [selectedWallet, setSelectedWallet] = useState6();
  const [walletStatus, setWalletStatus] = useState6(/* @__PURE__ */ new Map());
  useEffect3(() => {
    wallets2.forEach(async (item) => {
      const result = await item.checkInstalled();
      setWalletStatus((prev) => {
        const newS = new Map(prev);
        newS.set(item.getName(), result);
        return newS;
      });
    });
  }, [wallets2, setWalletStatus]);
  const resetSelection = () => {
    setSelectedWallet(void 0);
    setCurrentView(void 0);
  };
  const handleOpenChange = (open2) => {
    if (!open2) {
      resetSelection();
    }
    setModalOpen(open2);
    onOpenChange?.(open2);
  };
  const switchNetwork = async (wallet, target) => {
    wallet.switchNetwork(target).then(() => {
      connectWallet(wallet);
    });
  };
  const handleSelectedWallet = async (wallet) => {
    if (wallet.getName() === "OneKey") {
      connectWallet(wallet);
      return;
    }
    const target = await checkWalletNetwork(wallet, roochNetwork);
    if (target) {
      setTargetNetwork(target);
      setCurrentView("switch-network");
    } else {
      connectWallet(wallet);
    }
  };
  const connectWallet = (wallet) => {
    setCurrentView("connection-status");
    mutateAsync({ wallet }).then(() => {
      if (onSuccess) {
        onSuccess();
      }
      handleOpenChange(false);
    });
  };
  let modalContent;
  switch (currentView) {
    case "what-is-a-wallet":
      modalContent = /* @__PURE__ */ jsx23(WhatIsAWallet, {});
      break;
    case "switch-network":
      modalContent = /* @__PURE__ */ jsx23(
        SwitchNetworkView,
        {
          wallet: selectedWallet,
          targetNetWork: targetNetwork,
          switchNetwork,
          onSuccess: () => connectWallet(selectedWallet)
        }
      );
      break;
    case "connection-status":
      modalContent = /* @__PURE__ */ jsx23(
        ConnectionStatus,
        {
          selectedWallet,
          hadConnectionError: isError,
          info: (
            // TODO: Better solutions are needed to deal with it
            walletStatus.get("OneKey") && walletStatus.get("UniSat") && selectedWallet?.getName() === "UniSat" ? [
              "If UniSat does not work!",
              "Disable One Key Wallet, Refresh the page and try again."
            ] : void 0
          ),
          onRetryConnection: connectWallet
        }
      );
      break;
    case "install-status":
      modalContent = /* @__PURE__ */ jsx23(InstallStatus, { selectedWallet });
      break;
    default:
      modalContent = /* @__PURE__ */ jsx23(WhatIsAWallet, {});
  }
  return /* @__PURE__ */ jsxs10(
    Modal,
    {
      trigger,
      open: isModalOpen,
      defaultOpen,
      onOpenChange,
      children: [
        /* @__PURE__ */ jsxs10(
          "div",
          {
            className: clsx6(walletListContainer, {
              [walletListContainerWithViewSelected]: !!currentView
            }),
            children: [
              /* @__PURE__ */ jsxs10("div", { className: walletListContent, children: [
                /* @__PURE__ */ jsx23(Dialog2.Title, { className: title, asChild: true, children: /* @__PURE__ */ jsx23(Heading, { as: "h2", children: "Connect a Wallet" }) }),
                /* @__PURE__ */ jsx23(
                  WalletList,
                  {
                    selectedWalletName: selectedWallet?.getName(),
                    onSelect: (wallet) => {
                      if (selectedWallet?.getName() !== wallet.getName()) {
                        setSelectedWallet(wallet);
                        if (walletStatus.get(wallet.getName())) {
                          handleSelectedWallet(wallet);
                        } else {
                          setCurrentView("install-status");
                        }
                      }
                    }
                  }
                )
              ] }),
              /* @__PURE__ */ jsx23(
                "button",
                {
                  className: whatIsAWalletButton,
                  onClick: () => setCurrentView("what-is-a-wallet"),
                  type: "button",
                  children: "What is a Wallet?"
                }
              )
            ]
          }
        ),
        /* @__PURE__ */ jsxs10(
          "div",
          {
            className: clsx6(viewContainer, {
              [selectedViewContainer]: !!currentView
            }),
            children: [
              /* @__PURE__ */ jsx23("div", { className: backButtonContainer, children: /* @__PURE__ */ jsx23(IconButton, { type: "button", "aria-label": "Back", onClick: () => resetSelection(), children: /* @__PURE__ */ jsx23(BackIcon, {}) }) }),
              modalContent
            ]
          }
        )
      ]
    }
  );
}

// src/components/DropdownMenu.tsx
import clsx10 from "clsx";
import { useCallback as useCallback5, useEffect as useEffect11, useState as useState15 } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { ErrorValidateCantPayGasDeposit } from "@roochnetwork/rooch-sdk";

// src/components/DropdownMenu.css.ts
var addressContainer = "DropdownMenu_addressContainer__uignk47";
var connectedAddress = "DropdownMenu_connectedAddress__uignk40";
var menuContainer = "DropdownMenu_menuContainer__uignk41";
var menuContent = "DropdownMenu_menuContent__uignk42";
var menuItem = "DropdownMenu_menuItem__uignk43";
var progressBar2 = "DropdownMenu_progressBar__uignk46";
var rgasBalance = "DropdownMenu_rgasBalance__uignk48";
var switchMenuItem = "DropdownMenu_switchMenuItem__uignk44";

// src/components/icons/ChevronIcon.tsx
import { jsx as jsx24 } from "react/jsx-runtime";
function ChevronIcon(props) {
  return /* @__PURE__ */ jsx24("svg", { xmlns: "http://www.w3.org/2000/svg", width: 16, height: 16, fill: "none", ...props, children: /* @__PURE__ */ jsx24(
    "path",
    {
      stroke: "#A0B6C3",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      strokeWidth: 1.5,
      d: "m4 6 4 4 4-4"
    }
  ) });
}

// src/components/session-modal/SessionModal.tsx
import * as Dialog3 from "@radix-ui/react-dialog";
import clsx8 from "clsx";
import { useEffect as useEffect6, useState as useState9 } from "react";

// src/components/session-modal/SessionModal.css.ts
var backButtonContainer2 = "SessionModal_backButtonContainer__18jzcrv6";
var closeButtonContainer2 = "SessionModal_closeButtonContainer__18jzcrv7";
var content4 = "SessionModal_content__18jzcrv2";
var overlay2 = "SessionModal_overlay__18jzcrv0";
var selectedViewContainer2 = "SessionModal_selectedViewContainer__18jzcrv5";
var sessionListContainer = "SessionModal_sessionListContainer__18jzcrv9";
var sessionListContainerWithViewSelected = "SessionModal_sessionListContainerWithViewSelected__18jzcrva";
var sessionListContent = "SessionModal_sessionListContent__18jzcrv8";
var title5 = "SessionModal_title__18jzcrv1";
var viewContainer2 = "SessionModal_viewContainer__18jzcrv4";
var whatIsASessionButton = "SessionModal_whatIsASessionButton__18jzcrv3";

// src/components/session-modal/views/WhatIsASessionView.tsx
import { useEffect as useEffect4, useState as useState7 } from "react";
import { toShortStr } from "@roochnetwork/rooch-sdk";

// src/components/session-modal/views/WhatIsASessionView.css.ts
var actionButtonContainer = "WhatIsASessionView_actionButtonContainer__9cleo57";
var createSessionContainer = "WhatIsASessionView_createSessionContainer__9cleo53";
var createSessionContent = "WhatIsASessionView_createSessionContent__9cleo54";
var createSessionScopeContent = "WhatIsASessionView_createSessionScopeContent__9cleo55";
var createSessionStatus = "WhatIsASessionView_createSessionStatus__9cleo56";
var moreInfo = "WhatIsASessionView_moreInfo__9cleo58";
var sessionItemContent = "WhatIsASessionView_sessionItemContent__9cleo59";
var whatContent = "WhatIsASessionView_whatContent__9cleo51";
var whatMoreContent = "WhatIsASessionView_whatMoreContent__9cleo52";

// src/utils/time.ts
import dayjs from "dayjs";
function getUTCOffset() {
  const date = /* @__PURE__ */ new Date();
  const offset = -date.getTimezoneOffset();
  const hours = Math.floor(offset / 60);
  const minutes = offset % 60;
  return `UTC ${hours >= 0 ? "+" : "-"}${hours}:${minutes < 10 ? "0" : ""}${minutes}`;
}
var unix2str = (input) => {
  const timestampInSeconds = input > 1e12 ? input / 1e3 : input;
  return `${dayjs.unix(timestampInSeconds).format("MMM DD, YYYY HH:mm:ss")}`;
};
var second2Countdown = (input) => {
  const days = Math.floor(input / (24 * 3600));
  const hours = Math.floor(input % (24 * 3600) / 3600);
  const minutes = Math.floor(input % 3600 / 60);
  const secs = Math.floor(input % 60);
  return `${days} : ${hours} : ${minutes} : ${secs}`;
};

// src/components/session-modal/views/WhatIsASessionView.tsx
import { jsx as jsx25, jsxs as jsxs11 } from "react/jsx-runtime";
function WhatIsASessionView({ getTitle }) {
  const { start, finish } = useProgress();
  const { mutateAsync, isError } = useCreateSessionKey();
  const _sessionConf = useSessionStore((state) => state.sessionConf);
  const [model, setModel] = useState7("what");
  useEffect4(() => {
    getTitle(model === "what" ? "What is a session" : "Create session");
  }, [getTitle, model]);
  const createSession = () => {
    if (!_sessionConf) {
      return;
    }
    setModel("create");
    start();
    mutateAsync({ ..._sessionConf }).finally(() => finish());
  };
  return model === "what" ? /* @__PURE__ */ jsxs11("div", { className: whatContent, children: [
    /* @__PURE__ */ jsx25(Heading, { as: "h2", children: "What is a Session" }),
    /* @__PURE__ */ jsxs11("div", { className: whatContent, children: [
      /* @__PURE__ */ jsx25(Text, { weight: "medium", color: "muted", children: "Rooch's Session Key is a temporary key that facilitates users to interact with the chain." }),
      /* @__PURE__ */ jsx25(Text, { weight: "medium", color: "muted", children: "When interacting with Rooch applications, each application generates a session key. It has an expiration time and will become invalid if there is no interaction for a long time." }),
      /* @__PURE__ */ jsx25("div", { className: whatMoreContent, children: /* @__PURE__ */ jsx25(Text, { className: moreInfo, children: /* @__PURE__ */ jsx25(
        "a",
        {
          href: "https://rooch.network/learn/core-concepts/accounts/session-key",
          target: "_blank",
          rel: "noreferrer",
          children: "More Info"
        }
      ) }) })
    ] }),
    _sessionConf && /* @__PURE__ */ jsx25("div", { className: actionButtonContainer, children: /* @__PURE__ */ jsx25(Button, { type: "button", variant: "outline", onClick: createSession, children: "Create" }) })
  ] }) : /* @__PURE__ */ jsxs11("div", { className: createSessionContainer, children: [
    /* @__PURE__ */ jsx25(Heading, { as: "h2", children: "Info" }),
    /* @__PURE__ */ jsxs11("div", { className: createSessionContent, children: [
      /* @__PURE__ */ jsx25(Info, { name: "App name", value: _sessionConf.appName }),
      /* @__PURE__ */ jsx25(Info, { name: "App url", value: _sessionConf.appUrl }),
      /* @__PURE__ */ jsx25(
        Info,
        {
          name: "Expiration Interval",
          value: _sessionConf.maxInactiveInterval ? _sessionConf.maxInactiveInterval * 1e3 + Date.now() : 0
        }
      ),
      /* @__PURE__ */ jsxs11("div", { className: createSessionScopeContent, children: [
        /* @__PURE__ */ jsx25(Heading, { as: "h3", size: "sm", weight: "normal", children: "Scope" }),
        _sessionConf.scopes.slice(0, 3).map(
          (item) => typeof item === "string" ? item : `${item.address}::${item.module}::${item.function}`
        ).sort((a, b) => b.length - a.length).map((item) => {
          const _tmp = item.split("::");
          return /* @__PURE__ */ jsx25(Text, { children: _tmp[0].length > 3 ? toShortStr(item, {
            start: 12,
            end: 10 + _tmp[1].length + _tmp[2].length
          }) : item }, item);
        })
      ] }),
      /* @__PURE__ */ jsx25("div", { className: createSessionStatus, children: isError ? /* @__PURE__ */ jsx25(Text, { color: "danger", children: "Create failed" }) : /* @__PURE__ */ jsx25(Text, { color: "muted", children: "Confirm sign in the wallet..." }) }),
      isError ? /* @__PURE__ */ jsx25("div", { className: actionButtonContainer, children: /* @__PURE__ */ jsx25(Button, { type: "button", variant: "outline", onClick: createSession, children: "Retry Create" }) }) : null
    ] })
  ] });
}
function Info({ name, value }) {
  return /* @__PURE__ */ jsxs11("div", { className: sessionItemContent, children: [
    /* @__PURE__ */ jsx25(Text, { color: "danger", style: { width: "130px" }, children: name }),
    /* @__PURE__ */ jsx25(Text, { color: "muted", children: typeof value === "number" ? `${unix2str(value)} (${getUTCOffset()})` : value })
  ] });
}

// src/components/session-modal/session-list/SessionList.css.ts
var container10 = "SessionList_container__he8t610";

// src/components/session-modal/session-list/SessionListItem.tsx
import { clsx as clsx7 } from "clsx";

// src/components/session-modal/session-list/SessionListItem.css.ts
var container11 = "SessionListItem_container__128jgi30";
var selectedSessionItem = "SessionListItem_selectedSessionItem__128jgi32";
var sessionItem = "SessionListItem_sessionItem__128jgi31";

// src/components/session-modal/session-list/SessionListItem.tsx
import { toShortStr as toShortStr2 } from "@roochnetwork/rooch-sdk";
import { jsx as jsx26 } from "react/jsx-runtime";
function SessionListItem({ authKey, onClick, isSelected = false }) {
  return /* @__PURE__ */ jsx26("li", { className: container11, children: /* @__PURE__ */ jsx26(
    "button",
    {
      className: clsx7(sessionItem, { [selectedSessionItem]: isSelected }),
      type: "button",
      onClick,
      children: /* @__PURE__ */ jsx26(Heading, { size: "md", truncate: true, asChild: true, children: /* @__PURE__ */ jsx26("div", { children: toShortStr2(authKey, {
        start: 12,
        end: 6
      }) }) })
    }
  ) });
}

// src/components/session-modal/session-list/SessionList.tsx
import { jsx as jsx27, jsxs as jsxs12 } from "react/jsx-runtime";
function SessionList({ selectedSessionAuthKey, onSelect }) {
  const sessions = useSessions();
  return /* @__PURE__ */ jsxs12("ul", { className: container10, children: [
    /* @__PURE__ */ jsx27(
      SessionListItem,
      {
        authKey: selectedSessionAuthKey?.startsWith("0x") ? "What is a session" : selectedSessionAuthKey || "",
        isSelected: !selectedSessionAuthKey?.startsWith("0x"),
        onClick: () => onSelect()
      },
      "new"
    ),
    sessions.map((session) => /* @__PURE__ */ jsx27(
      SessionListItem,
      {
        authKey: session.getAuthKey(),
        isSelected: session.getAuthKey() === selectedSessionAuthKey,
        onClick: () => onSelect(session)
      },
      session.getAuthKey()
    ))
  ] });
}

// src/components/session-modal/views/SessionView.tsx
import { useEffect as useEffect5, useMemo as useMemo3, useState as useState8 } from "react";
import { toShortStr as toShortStr3 } from "@roochnetwork/rooch-sdk";

// src/components/session-modal/views/SessionView.css.ts
var container12 = "SessionView_container__t5n37e0";
var content5 = "SessionView_content__t5n37e3";
var moreContent = "SessionView_moreContent__t5n37e6";
var moreInfo2 = "SessionView_moreInfo__t5n37e7";
var removeBtn = "SessionView_removeBtn__t5n37e9";
var removeButtonContainer = "SessionView_removeButtonContainer__t5n37e8";
var scopeContent = "SessionView_scopeContent__t5n37e5";
var sessionItemContent2 = "SessionView_sessionItemContent__t5n37e4";

// src/components/SessionKeyGuard.tsx
import { Fragment, jsx as jsx28 } from "react/jsx-runtime";
function SessionKeyGuard({ children, sessionConf, onClick }) {
  const session = useCurrentSession();
  const curSession = useCurrentSession();
  const curAddress = useCurrentAddress();
  const _sessionConf = useSessionStore((state) => state.sessionConf);
  const { mutate } = useCreateSessionKey();
  const handleCreateSession = () => {
    if (curSession && !curSession.isSessionExpired()) {
      onClick();
      return;
    }
    const _conf = _sessionConf || sessionConf;
    if (_conf) {
      mutate({ ..._conf });
    } else {
      onClick();
    }
  };
  return /* @__PURE__ */ jsx28(Fragment, { children: curAddress ? /* @__PURE__ */ jsx28(
    Button,
    {
      asChild: true,
      onClick: () => {
        handleCreateSession();
      },
      children
    }
  ) : /* @__PURE__ */ jsx28(
    ConnectModal,
    {
      trigger: children,
      onSuccess: () => {
        if (!session) {
          handleCreateSession();
        }
      }
    }
  ) });
}

// src/components/session-modal/views/SessionView.tsx
import { jsx as jsx29, jsxs as jsxs13 } from "react/jsx-runtime";
function SessionView({ selectedSession, removedCallback }) {
  const { loading, start, finish } = useProgress();
  const { mutateAsync, isError } = useRemoveSession();
  const [timeRemaining, setTimeRemaining] = useState8(-1);
  useEffect5(() => {
    if (selectedSession.isSessionExpired()) {
      setTimeRemaining(0);
      return;
    }
    const interval = setInterval(() => {
      const now = Date.now() / 1e3;
      setTimeRemaining(
        selectedSession.getLastActiveTime() / 1e3 + selectedSession.maxInactiveInterval - now
      );
    }, 1e3);
    return () => clearInterval(interval);
  }, [selectedSession]);
  const removeSession = (session) => {
    start();
    mutateAsync({
      authKey: session.getAuthKey()
    }).then(() => removedCallback(session)).finally(finish);
  };
  const scopes = useMemo3(() => {
    return selectedSession.scopes.slice(0, 3).sort((a, b) => b.length - a.length).map((item) => {
      const _tmp = item.split("::");
      return _tmp[0].length > 3 ? toShortStr3(item, {
        start: 12,
        end: 10 + _tmp[1].length + _tmp[2].length
      }) : item;
    });
  }, [selectedSession]);
  return /* @__PURE__ */ jsxs13("div", { className: container12, children: [
    /* @__PURE__ */ jsx29(Heading, { as: "h2", children: "Info" }),
    /* @__PURE__ */ jsxs13("div", { className: content5, children: [
      /* @__PURE__ */ jsx29(Info2, { name: "Create At:", value: selectedSession.localCreateSessionTime }),
      /* @__PURE__ */ jsx29(Info2, { name: "Last Active Time:", value: selectedSession.getLastActiveTime() }),
      /* @__PURE__ */ jsx29(
        Info2,
        {
          name: "Time Remaining:",
          value: timeRemaining === 0 ? "Expired" : timeRemaining === -1 ? "calculating" : second2Countdown(timeRemaining)
        }
      ),
      /* @__PURE__ */ jsxs13("div", { className: scopeContent, children: [
        /* @__PURE__ */ jsx29(Heading, { as: "h3", size: "sm", weight: "normal", children: "Scope" }),
        scopes.map((scope) => /* @__PURE__ */ jsx29(Text, { children: scope }, scope))
      ] }),
      /* @__PURE__ */ jsx29("div", { className: moreContent, children: /* @__PURE__ */ jsx29(Text, { className: moreInfo2, children: /* @__PURE__ */ jsx29("a", { href: "https://portal.rooch.network/settings", target: "_blank", rel: "noreferrer", children: "More Info" }) }) }),
      /* @__PURE__ */ jsx29("div", { className: removeButtonContainer, children: /* @__PURE__ */ jsx29(
        SessionKeyGuard,
        {
          onClick: () => {
            removeSession(selectedSession);
          },
          children: /* @__PURE__ */ jsx29(Button, { className: removeBtn, disabled: loading, type: "button", variant: "outline", children: isError ? "Retry Remove" : "Remove" })
        }
      ) })
    ] })
  ] });
}
function Info2({ name, value }) {
  return /* @__PURE__ */ jsxs13("div", { className: sessionItemContent2, children: [
    /* @__PURE__ */ jsx29(Text, { color: "danger", style: { width: "130px" }, children: name }),
    /* @__PURE__ */ jsx29(Text, { color: "muted", children: typeof value === "number" ? `${unix2str(value)}${getUTCOffset()}` : value })
  ] });
}

// src/components/session-modal/SessionModal.tsx
import { jsx as jsx30, jsxs as jsxs14 } from "react/jsx-runtime";
function SessionModal({ trigger, open, defaultOpen, onOpenChange }) {
  const sessions = useSessions();
  const currentSession = useCurrentSession();
  const [isModalOpen, setModalOpen] = useState9(open ?? defaultOpen);
  const [currentView, setCurrentView] = useState9("what-is-a-session");
  const [selectedSession, setSelectedSession] = useState9();
  const [sessionTitle, setSessionTitle] = useState9();
  const resetSelection = () => {
    setSelectedSession(void 0);
    setCurrentView("what-is-a-session");
  };
  const handleOpenChange = (open2) => {
    if (!open2) {
      resetSelection();
    }
    setModalOpen(open2);
    onOpenChange?.(open2);
  };
  useEffect6(() => {
    if (currentSession) {
      setSelectedSession(currentSession);
      setCurrentView("session-status");
    } else {
      setSelectedSession(void 0);
      setCurrentView("what-is-a-session");
    }
  }, [currentSession]);
  let modalContent;
  switch (currentView) {
    case "session-status":
      modalContent = /* @__PURE__ */ jsx30(
        SessionView,
        {
          selectedSession,
          removedCallback: (session) => {
            if (sessions.length > 1) {
              sessions.forEach((v, i) => {
                if (v.getAuthKey() === session.getAuthKey()) {
                  if (i - 1 < 0) {
                    return;
                  }
                  setSelectedSession(sessions[i - 1]);
                  setCurrentView("session-status");
                }
              });
            }
          }
        }
      );
      break;
    default:
      modalContent = /* @__PURE__ */ jsx30(
        WhatIsASessionView,
        {
          getTitle: (title7) => {
            setSessionTitle(title7);
          }
        }
      );
  }
  useEffect6(() => {
    if (sessions.length === 0) {
      setCurrentView("what-is-a-session");
    }
  }, [sessions]);
  return /* @__PURE__ */ jsxs14(Dialog3.Root, { open: open ?? isModalOpen, onOpenChange: handleOpenChange, children: [
    /* @__PURE__ */ jsx30(Dialog3.Trigger, { asChild: true, children: trigger }),
    /* @__PURE__ */ jsx30(Dialog3.Portal, { children: /* @__PURE__ */ jsx30(StyleMarker, { children: /* @__PURE__ */ jsx30(Dialog3.Overlay, { className: overlay2, children: /* @__PURE__ */ jsxs14(Dialog3.Content, { className: content4, "aria-describedby": void 0, children: [
      /* @__PURE__ */ jsxs14(
        "div",
        {
          className: clsx8(sessionListContainer, {
            [sessionListContainerWithViewSelected]: !!currentView
          }),
          children: [
            /* @__PURE__ */ jsxs14("div", { className: sessionListContent, children: [
              /* @__PURE__ */ jsx30(Dialog3.Title, { className: title5, asChild: true, children: /* @__PURE__ */ jsx30(Heading, { as: "h2", children: "Session Manager" }) }),
              /* @__PURE__ */ jsx30(
                SessionList,
                {
                  selectedSessionAuthKey: selectedSession?.getAuthKey() || sessionTitle,
                  onSelect: (session) => {
                    if (!session) {
                      setSelectedSession(void 0);
                      setCurrentView("what-is-a-session");
                    } else {
                      setSelectedSession(session);
                      setCurrentView("session-status");
                    }
                  }
                }
              )
            ] }),
            /* @__PURE__ */ jsx30(
              "button",
              {
                className: whatIsASessionButton,
                onClick: () => setCurrentView("what-is-a-session"),
                type: "button",
                children: "What is a session?"
              }
            )
          ]
        }
      ),
      /* @__PURE__ */ jsxs14(
        "div",
        {
          className: clsx8(viewContainer2, {
            [selectedViewContainer2]: !!currentView
          }),
          children: [
            /* @__PURE__ */ jsx30("div", { className: backButtonContainer2, children: /* @__PURE__ */ jsx30(IconButton, { type: "button", "aria-label": "Back", onClick: () => resetSelection(), children: /* @__PURE__ */ jsx30(BackIcon, {}) }) }),
            /* @__PURE__ */ jsx30(ProgressProvider, { children: modalContent })
          ]
        }
      ),
      /* @__PURE__ */ jsx30(Dialog3.Close, { className: closeButtonContainer2, asChild: true, children: /* @__PURE__ */ jsx30(IconButton, { type: "button", "aria-label": "Close", children: /* @__PURE__ */ jsx30(CloseIcon, {}) }) })
    ] }) }) }) })
  ] });
}

// src/components/fauct-modal/FaucetModal.tsx
import * as Dialog4 from "@radix-ui/react-dialog";
import { useState as useState11 } from "react";

// src/components/fauct-modal/FaucetModal.css.ts
var closeButtonContainer3 = "FaucetModal_closeButtonContainer__18sfpht7";
var content6 = "FaucetModal_content__18sfpht2";
var overlay3 = "FaucetModal_overlay__18sfpht0";

// src/components/fauct-modal/views/FaucetView.tsx
import { useCallback as useCallback4, useEffect as useEffect7, useState as useState10 } from "react";

// src/components/fauct-modal/env.ts
var ENVS = {
  main: {
    faucet: {
      Url: "https://main-faucet.rooch.network",
      CA: "0x701c21bf1c8cd5af8c42983890d8ca55e7a820171b8e744c13f2d9998bf76cc3",
      Obj: "0xd5723eda84f691ae2623da79312c7909b1737c5b3866ecc5dbd6aa21718ff15d"
    },
    inviter: {
      CA: "0x701c21bf1c8cd5af8c42983890d8ca55e7a820171b8e744c13f2d9998bf76cc3",
      Module: "invitation",
      Conf: "InvitationConf"
    }
  },
  test: {
    faucet: {
      Url: "https://test-faucet.rooch.network",
      CA: "0x701c21bf1c8cd5af8c42983890d8ca55e7a820171b8e744c13f2d9998bf76cc3",
      Obj: "0xd5723eda84f691ae2623da79312c7909b1737c5b3866ecc5dbd6aa21718ff15d"
    },
    inviter: {
      CA: "0x701c21bf1c8cd5af8c42983890d8ca55e7a820171b8e744c13f2d9998bf76cc3",
      Module: "invitation",
      Conf: "InvitationConf"
    }
  }
};

// src/components/fauct-modal/views/FaucetView.tsx
import { Args, fixedBalance, stringToBytes, toHEX } from "@roochnetwork/rooch-sdk";
import { jsx as jsx31, jsxs as jsxs15 } from "react/jsx-runtime";
var FAUCET_NOT_OPEN = "Oops! The faucet isn't open right now. \u{1F6B0}\u{1F512} Check back later!";
var INVALID_UTXO = "Hmm, something's not right with the UTXO. \u{1F914}\u{1F50D} Maybe double-check it?";
var FAUCET_NOT_ENOUGH_RGAS = "Looks like we're running low on RGas. \u26FD\uFE0F\u{1F4A8} Please try again soon!";
var ALREADY_CLAIMED = "You've already claim your RGas! \u{1F389}\u{1F60E} Enjoy!";
var UTXO_VALUE_IS_ZERO = "The UTXO value is zero. \u{1F4B8}\u{1F9D0} Please ensure it's got some value!";
var unknowError = "An unknown error occurred. Please try again later. \u{1F937}\u200D\u2642\uFE0F";
var ERROR_MSG = {
  1: FAUCET_NOT_OPEN,
  2: INVALID_UTXO,
  3: FAUCET_NOT_ENOUGH_RGAS,
  4: ALREADY_CLAIMED,
  5: UTXO_VALUE_IS_ZERO
};
function FaucetView({ inviter, swapRGas }) {
  const client = useRoochClient();
  const { wallet } = useCurrentWallet();
  const currentNetwork = useCurrentNetwork();
  const currentAddress = useCurrentAddress();
  const [loading, setLoading] = useState10();
  const { start, finish } = useProgress();
  const [faucetAward, setFaucetAward] = useState10(0);
  const [errorMessage, setErrorMessage] = useState10();
  const [claimedMessage, setClaimedMessage] = useState10();
  const [needCheck, setNeedCheck] = useState10(false);
  const triggerRequest = useTriggerRequest();
  const env = currentNetwork === "mainnet" ? ENVS.main : ENVS.test;
  const startLoading = useCallback4(
    (msg) => {
      setLoading(msg);
      start();
    },
    [start]
  );
  const finishLoading = useCallback4(() => {
    finish(() => {
      setLoading(void 0);
    });
  }, [finish]);
  const checkClaim = useCallback4(() => {
    if (!currentAddress) {
      return;
    }
    setFaucetAward(0);
    setErrorMessage(void 0);
    setClaimedMessage(void 0);
    startLoading("Verifying your eligibility for gas... \u{1F575}\uFE0F\u200D\u2640\uFE0F\u2728");
    client.queryUTXO({
      filter: {
        owner: currentAddress.toStr()
      }
    }).then(async (result) => {
      const utxoIds = result.data.map((item) => item.id);
      if (utxoIds.length > 0) {
        const result2 = await client.executeViewFunction({
          target: `${env.faucet.CA}::gas_faucet::check_claim`,
          args: [
            Args.objectId(env.faucet.Obj),
            Args.address(currentAddress.genRoochAddress().toHexAddress()),
            Args.vec("objectId", utxoIds)
          ]
        });
        if (result2.vm_status === "Executed") {
          const gas = Number(fixedBalance(Number(result2.return_values[0].decoded_value), 8));
          setFaucetAward(gas);
        } else if ("MoveAbort" in result2.vm_status) {
          setErrorMessage(ERROR_MSG[Number(result2.vm_status.MoveAbort.abort_code)]);
        }
      } else {
        setErrorMessage(INVALID_UTXO);
      }
    }).catch((e) => {
      console.log(e);
      setErrorMessage(unknowError);
    }).finally(() => {
      finishLoading();
      setNeedCheck(false);
    });
  }, [client, currentAddress, env.faucet.CA, env.faucet.Obj, finishLoading, startLoading]);
  useEffect7(() => {
    checkClaim();
  }, [checkClaim]);
  const handleClaim = async () => {
    if (errorMessage) {
      swapRGas();
      return;
    }
    try {
      startLoading("Hang tight, your RGas is zooming your way! \u{1F680}\u2728");
      const response = await (inviter ? claimWithInviter() : claim());
      if (!response.ok) {
        const data = await response.json();
        if (response.status === 500 && data.error.includes("UTXO value is zero")) {
          setErrorMessage(ALREADY_CLAIMED);
          return;
        }
        setErrorMessage(unknowError);
        return;
      }
      const d = await response.json();
      const awardRGAS = fixedBalance(d.gas || 0, 8);
      setClaimedMessage(`Awesome! You've got ${awardRGAS} RGas in your pocket! \u{1F680}\u{1F389}`);
      setNeedCheck(true);
      triggerRequest("success");
    } catch (e) {
      if ("message" in e) {
        setErrorMessage(e.message);
      } else {
        setErrorMessage(unknowError);
      }
    } finally {
      finishLoading();
    }
  };
  const claim = () => {
    const payload = JSON.stringify({
      claimer: currentAddress?.toStr()
    });
    return fetch(`${env.faucet.Url}/faucet`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: payload
    });
  };
  const claimWithInviter = async () => {
    const result = await client.queryObjectStates({
      filter: {
        object_type: `${env.inviter.CA}::${env.inviter.Module}::${env.inviter.Conf}`
      }
    });
    if (result && result.data.length > 0 && result.data[0].decoded_value?.value.is_open === true) {
      const pk = wallet.getPublicKey().toBytes();
      const signMsg = "Welcome to use Rooch! Hold BTC Claim your RGas.";
      const sign = await wallet.sign(stringToBytes("utf8", signMsg));
      const payload = JSON.stringify({
        claimer: currentAddress?.toStr(),
        inviter,
        claimer_sign: toHEX(sign),
        public_key: toHEX(pk),
        message: signMsg
      });
      return await fetch(`${env.faucet.Url}/faucet-inviter`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: payload
      });
    } else {
      return claim();
    }
  };
  return /* @__PURE__ */ jsxs15("div", { className: container8, children: [
    /* @__PURE__ */ jsx31(Heading, { as: "h2", children: "Rooch Faucet" }),
    /* @__PURE__ */ jsx31("div", { className: content3, children: /* @__PURE__ */ jsxs15(Text, { weight: "medium", color: "muted", children: [
      loading,
      !loading && errorMessage,
      !loading && claimedMessage,
      !loading && !claimedMessage && faucetAward ? `Yay! You can claim ${faucetAward} RGas! \u{1F389}\u{1F4A7}` : void 0
    ] }) }),
    /* @__PURE__ */ jsx31("div", { className: createButtonContainer, children: /* @__PURE__ */ jsx31(
      Button,
      {
        disabled: loading !== void 0,
        type: "button",
        variant: "outline",
        onClick: needCheck ? checkClaim : handleClaim,
        children: errorMessage ? "Swap RGas" : needCheck ? "Check" : "Claim"
      }
    ) })
  ] });
}

// src/components/fauct-modal/FaucetModal.tsx
import { jsx as jsx32, jsxs as jsxs16 } from "react/jsx-runtime";
function FaucetModal({
  trigger,
  inviter,
  swapRGas,
  open,
  defaultOpen,
  onOpenChange
}) {
  const [isModalOpen, setModalOpen] = useState11(open ?? defaultOpen);
  const handleOpenChange = (open2) => {
    setModalOpen(open2);
    onOpenChange?.(open2);
  };
  return /* @__PURE__ */ jsxs16(Dialog4.Root, { open: open ?? isModalOpen, onOpenChange: handleOpenChange, children: [
    /* @__PURE__ */ jsx32(Dialog4.Trigger, { asChild: true, children: trigger }),
    /* @__PURE__ */ jsx32(Dialog4.Portal, { children: /* @__PURE__ */ jsx32(StyleMarker, { children: /* @__PURE__ */ jsx32(Dialog4.Overlay, { className: overlay3, children: /* @__PURE__ */ jsxs16(Dialog4.Content, { className: content6, "aria-describedby": void 0, children: [
      /* @__PURE__ */ jsx32(ProgressProvider, { children: /* @__PURE__ */ jsx32(FaucetView, { inviter, swapRGas }) }),
      /* @__PURE__ */ jsx32(Dialog4.Close, { className: closeButtonContainer3, asChild: true, children: /* @__PURE__ */ jsx32(IconButton, { type: "button", "aria-label": "Close", children: /* @__PURE__ */ jsx32(CloseIcon, {}) }) })
    ] }) }) }) })
  ] });
}

// src/components/swap-gas-modal/views/SwapGasView.tsx
import { Decimal } from "decimal.js";
import { useEffect as useEffect9, useState as useState13 } from "react";
import { Args as Args2, fixedBalance as fixedBalance2 } from "@roochnetwork/rooch-sdk";

// src/components/swap-gas-modal/views/SwapGasView.css.ts
var container13 = "SwapGasView_container__bmmf7w0";
var infoContainer = "SwapGasView_infoContainer__bmmf7w1";
var inputContainer = "SwapGasView_inputContainer__bmmf7w2";
var separator = "SwapGasView_separator__bmmf7w3";

// src/components/swap-gas-modal/env.ts
var MarketEev = {
  main: {
    marketCA: "0x701c21bf1c8cd5af8c42983890d8ca55e7a820171b8e744c13f2d9998bf76cc3",
    btcGasAddress: "bc1prcajaj9n7e29u4dfp33x3hcf52yqeegspdpcd79pqu4fpr6llx4sugkfjt"
  },
  test: {
    marketCA: "0x701c21bf1c8cd5af8c42983890d8ca55e7a820171b8e744c13f2d9998bf76cc3",
    btcGasAddress: "tb1prcajaj9n7e29u4dfp33x3hcf52yqeegspdpcd79pqu4fpr6llx4stqqxgy"
  }
};

// src/components/ui/View.css.ts
var actionButtonContainer2 = "View_actionButtonContainer__1lnj8w33";
var container14 = "View_container__1lnj8w30";
var content7 = "View_content__1lnj8w32";
var title6 = "View_title__1lnj8w31";

// src/components/ui/View.tsx
import { jsx as jsx33, jsxs as jsxs17 } from "react/jsx-runtime";
function View({ title: title7, disabledAction, actionText, actionOnClick, children }) {
  const { loading } = useProgress();
  return /* @__PURE__ */ jsxs17("div", { className: container14, children: [
    /* @__PURE__ */ jsx33(Heading, { as: "h2", className: title6, children: title7 }),
    /* @__PURE__ */ jsx33("div", { className: content7, children }),
    actionText && /* @__PURE__ */ jsx33("div", { className: actionButtonContainer2, children: /* @__PURE__ */ jsx33(
      Button,
      {
        disabled: loading || disabledAction,
        type: "button",
        variant: "outline",
        onClick: actionOnClick,
        children: actionText
      }
    ) })
  ] });
}

// src/components/ui/Input.tsx
import { Slot as Slot6 } from "@radix-ui/react-slot";
import clsx9 from "clsx";
import { forwardRef as forwardRef6 } from "react";

// src/components/ui/Input.css.ts
import { createRuntimeFn as _7a4684 } from "@vanilla-extract/recipes/createRuntimeFn";
var inputVariants = _7a4684({ defaultClassName: "Input_inputVariants__1878es40", variantClassNames: { variant: { primary: "Input_inputVariants_variant_primary__1878es41", outline: "Input_inputVariants_variant_outline__1878es42" }, size: { md: "Input_inputVariants_size_md__1878es43", lg: "Input_inputVariants_size_lg__1878es44" } }, defaultVariants: { variant: "primary", size: "md" }, compoundVariants: [] });

// src/components/ui/Input.tsx
import { jsx as jsx34 } from "react/jsx-runtime";
var Input = forwardRef6(
  ({ className, variant, size, asChild = false, ...props }, forwardedRef) => {
    const Comp = asChild ? Slot6 : "input";
    return /* @__PURE__ */ jsx34(
      Comp,
      {
        ...props,
        className: clsx9(inputVariants({ variant, size }), className),
        ref: forwardedRef
      }
    );
  }
);
Input.displayName = "Button";

// src/components/ui/Card.css.ts
var card = "Card_card__1bu3cob0";
var cardBody = "Card_cardBody__1bu3cob4";
var cardFooter = "Card_cardFooter__1bu3cob5";
var cardHeader = "Card_cardHeader__1bu3cob1";

// src/components/ui/Card.tsx
import { jsx as jsx35, jsxs as jsxs18 } from "react/jsx-runtime";
function Card({ header, headerRight, children, footer }) {
  return /* @__PURE__ */ jsxs18("div", { className: card, children: [
    (header || headerRight) && /* @__PURE__ */ jsxs18("div", { className: cardHeader, children: [
      /* @__PURE__ */ jsx35(Heading, { as: "h3", size: "sm", weight: "normal", children: header }),
      headerRight && /* @__PURE__ */ jsx35(Text, { weight: "medium", color: "muted", children: headerRight })
    ] }),
    /* @__PURE__ */ jsx35("div", { className: cardBody, children }),
    footer && /* @__PURE__ */ jsx35("div", { className: cardFooter, children: footer })
  ] });
}

// src/components/icons/BitcoinIcon.tsx
import { jsx as jsx36, jsxs as jsxs19 } from "react/jsx-runtime";
function BitcoinIcon(props) {
  return /* @__PURE__ */ jsx36(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      xmlSpace: "preserve",
      width: "24",
      height: "24",
      version: "1.1",
      shapeRendering: "geometricPrecision",
      textRendering: "geometricPrecision",
      imageRendering: "optimizeQuality",
      fillRule: "evenodd",
      clipRule: "evenodd",
      viewBox: "0 0 4091.27 4091.73",
      ...props,
      children: /* @__PURE__ */ jsxs19("g", { id: "Layer_x0020_1", children: [
        /* @__PURE__ */ jsx36("metadata", { id: "CorelCorpID_0Corel-Layer" }),
        /* @__PURE__ */ jsxs19("g", { id: "_1421344023328", children: [
          /* @__PURE__ */ jsx36(
            "path",
            {
              fill: "#F7931A",
              fillRule: "nonzero",
              d: "M4030.06 2540.77c-273.24,1096.01 -1383.32,1763.02 -2479.46,1489.71 -1095.68,-273.24 -1762.69,-1383.39 -1489.33,-2479.31 273.12,-1096.13 1383.2,-1763.19 2479,-1489.95 1096.06,273.24 1763.03,1383.51 1489.76,2479.57l0.02 -0.02z"
            }
          ),
          /* @__PURE__ */ jsx36(
            "path",
            {
              fill: "white",
              fillRule: "nonzero",
              d: "M2947.77 1754.38c40.72,-272.26 -166.56,-418.61 -450,-516.24l91.95 -368.8 -224.5 -55.94 -89.51 359.09c-59.02,-14.72 -119.63,-28.59 -179.87,-42.34l90.16 -361.46 -224.36 -55.94 -92 368.68c-48.84,-11.12 -96.81,-22.11 -143.35,-33.69l0.26 -1.16 -309.59 -77.31 -59.72 239.78c0,0 166.56,38.18 163.05,40.53 90.91,22.69 107.35,82.87 104.62,130.57l-104.74 420.15c6.26,1.59 14.38,3.89 23.34,7.49 -7.49,-1.86 -15.46,-3.89 -23.73,-5.87l-146.81 588.57c-11.11,27.62 -39.31,69.07 -102.87,53.33 2.25,3.26 -163.17,-40.72 -163.17,-40.72l-111.46 256.98 292.15 72.83c54.35,13.63 107.61,27.89 160.06,41.3l-92.9 373.03 224.24 55.94 92 -369.07c61.26,16.63 120.71,31.97 178.91,46.43l-91.69 367.33 224.51 55.94 92.89 -372.33c382.82,72.45 670.67,43.24 791.83,-303.02 97.63,-278.78 -4.86,-439.58 -206.26,-544.44 146.69,-33.83 257.18,-130.31 286.64,-329.61l-0.07 -0.05zm-512.93 719.26c-69.38,278.78 -538.76,128.08 -690.94,90.29l123.28 -494.2c152.17,37.99 640.17,113.17 567.67,403.91zm69.43 -723.3c-63.29,253.58 -453.96,124.75 -580.69,93.16l111.77 -448.21c126.73,31.59 534.85,90.55 468.94,355.05l-0.02 0z"
            }
          )
        ] })
      ] })
    }
  );
}

// src/components/icons/RGasIcon.tsx
import { jsx as jsx37, jsxs as jsxs20 } from "react/jsx-runtime";
function RGasIcon(props) {
  return /* @__PURE__ */ jsxs20(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: "0 0 500 500",
      width: 24,
      height: 24,
      ...props,
      children: [
        /* @__PURE__ */ jsx37(
          "circle",
          {
            cx: "250",
            cy: "250",
            r: "250",
            fill: "#b2ff04"
          }
        ),
        /* @__PURE__ */ jsx37(
          "path",
          {
            fill: "#006840",
            d: "M346.39,403.15c-26.17-23.18-51.58-45.69-77.73-68.86-.13,2.58-.28,4.18-.29,5.77-.19,30.24-.36,60.48-.54,90.73q-.04,6.48-6.74,6.45c-8.78-.05-17.57-.27-26.35-.07-3.46,.08-4.36-.83-4.32-4.3,.33-31.62,.46-63.23,.64-94.85,0-1.05,.01-2.09,.02-4.12-26.46,22.99-52.29,45.44-78.06,67.84-1.6-.72-.96-1.95-.96-2.88,.04-14.97,.2-29.94,.17-44.9,0-2.36,.75-3.86,2.52-5.42,28.99-25.57,57.91-51.22,86.85-76.84,.67-.59,1.45-1.07,2.4-1.76-1.89-1.66-3.64-1.09-5.18-1.1-27.12-.2-54.24-.43-81.36-.41-3.91,0-4.88-1.05-4.74-4.83,.34-9.46,.42-18.94,.16-28.4-.11-3.83,1.29-4.39,4.68-4.35,26.96,.28,53.93,.38,80.9,.54,1.68,0,3.37,.02,5.83,.03-2.08-3.59-5.06-5.5-7.53-7.74-27-24.56-54.06-49.05-81.17-73.49-1.49-1.35-2.15-2.64-2.13-4.66,.17-16.03,.23-32.07,.33-49.24,26.24,23.34,51.86,46.12,78.27,69.61,.01-2.46,.02-4.06,.03-5.65,.18-31.01,.43-62.01,.45-93.02,0-3.71,1.1-4.55,4.65-4.44,9.39,.29,18.8,.42,28.19,.14,4.06-.12,4.69,1.28,4.65,4.93-.33,30.7-.44,61.4-.62,92.1,0,1.63-.02,3.27-.04,6.19,26.52-23.13,52.16-45.5,77.98-68.01,1.04,1.6,.64,2.99,.64,4.27-.05,13.9-.29,27.8-.13,41.7,.05,3.86-1.14,6.41-4.05,8.97-28.31,24.95-56.48,50.05-84.69,75.1-.78,.69-1.52,1.41-2.45,2.27,1.66,1.71,3.6,1.03,5.27,1.04,26.96,.21,53.93,.41,80.9,.44,3.42,0,4.77,.6,4.62,4.41-.37,9.61-.37,19.24-.19,28.86,.06,3.38-.77,4.35-4.31,4.31-27.12-.32-54.24-.4-81.36-.56-1.64,0-3.28-.02-6.14-.04,8.77,7.94,16.64,15.09,24.54,22.22,21.63,19.52,43.26,39.04,64.91,58.53,1.08,.97,1.81,1.89,1.8,3.46-.14,16.32-.22,32.64-.32,50.05Z"
          }
        )
      ]
    }
  );
}

// src/components/util/debounce.ts
import { useEffect as useEffect8, useState as useState12 } from "react";
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState12(value);
  useEffect8(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  return debouncedValue;
}

// src/components/swap-gas-modal/views/SwapGasView.tsx
import { jsx as jsx38, jsxs as jsxs21 } from "react/jsx-runtime";
var btcDecimals = 1e8;
function SwapGasView() {
  const { wallet } = useCurrentWallet();
  const currentAddress = useCurrentAddress();
  const client = useRoochClient();
  const network = useCurrentNetwork();
  const env = network === "mainnet" ? MarketEev.main : MarketEev.test;
  const [rate, setRate] = useState13(0);
  const [to, setTo] = useState13(0);
  const [rGasBalance, setRGasBalance] = useState13(0);
  const [bitcoinBalance, setBitcoinBalance] = useState13(0);
  const [form, setForm] = useState13("0.0");
  const debouncedForm = useDebounce(form, 500);
  const { start, finish } = useProgress();
  const [errorMessage, setErrorMessage] = useState13();
  useEffect9(() => {
    if (!currentAddress) {
      return;
    }
    const fetchBalances = async () => {
      try {
        const bitcoinResult = await wallet?.getBalance();
        const rGasResult = await client.getBalance({
          owner: currentAddress,
          coinType: "0x3::gas_coin::RGas"
        });
        setBitcoinBalance(bitcoinResult?.confirmed || 0);
        setRGasBalance(rGasResult.fixedBalance);
        finish();
      } catch (error) {
        setErrorMessage("Failed to fetch balances. Please try again later.");
      }
    };
    start();
    fetchBalances().finally(() => finish());
  }, [client, currentAddress, wallet, start, finish]);
  useEffect9(() => {
    const fromNumber = Number(debouncedForm);
    if (fromNumber <= 0) {
      return;
    }
    start();
    const stas = fromNumber * btcDecimals;
    client.executeViewFunction({
      address: env.marketCA,
      module: "gas_market",
      function: "btc_to_rgas",
      args: [Args2.u64(BigInt(stas))]
    }).then((result) => {
      const value = Number(result.return_values?.[0]?.decoded_value || 0) || 0;
      const s = new Decimal(value).div(stas.toString()).toNumber();
      setTo(value);
      setRate(s);
    }).finally(() => finish());
  }, [client, finish, debouncedForm, env.marketCA, start]);
  const swap = () => {
    start();
    wallet?.sendBtc({
      toAddress: env.btcGasAddress,
      satoshis: Number(Number(debouncedForm) * btcDecimals)
    }).finally(() => finish());
  };
  return /* @__PURE__ */ jsxs21(View, { title: "Swap Gas", actionText: "Swap", actionOnClick: swap, children: [
    /* @__PURE__ */ jsxs21("div", { className: container13, children: [
      /* @__PURE__ */ jsx38(Card, { header: "From", headerRight: `Balance: ${fixedBalance2(bitcoinBalance, 9)}`, children: /* @__PURE__ */ jsxs21("div", { className: inputContainer, children: [
        /* @__PURE__ */ jsx38(
          Input,
          {
            type: "number",
            value: form,
            onChange: (v) => {
              const value = v.target.value;
              if (/^\d*\.?\d*$/.test(value)) {
                setForm(value);
              }
            }
          }
        ),
        /* @__PURE__ */ jsx38(BitcoinIcon, {})
      ] }) }),
      /* @__PURE__ */ jsx38(Card, { header: "To", headerRight: `Balance: ${rGasBalance}`, children: /* @__PURE__ */ jsxs21("div", { className: inputContainer, children: [
        /* @__PURE__ */ jsx38(
          Input,
          {
            disabled: true,
            value: Intl.NumberFormat("en-us").format(Number(fixedBalance2(to, 8).toFixed(2)))
          }
        ),
        /* @__PURE__ */ jsx38(RGasIcon, {})
      ] }) })
    ] }),
    /* @__PURE__ */ jsxs21("div", { className: infoContainer, children: [
      /* @__PURE__ */ jsxs21(Heading, { as: "h3", size: "sm", weight: "normal", children: [
        errorMessage,
        !errorMessage && `1 BTC = ${rate === 0 ? "\u267E\uFE0F" : Intl.NumberFormat("en-us").format(rate)} RGas`
      ] }),
      /* @__PURE__ */ jsx38("div", { className: separator }),
      /* @__PURE__ */ jsx38(Text, { weight: "medium", color: "muted", children: "Estimated rate, the actual amount received depends on the rate at the time of transaction confirmation block." })
    ] })
  ] });
}

// src/components/swap-gas-modal/SwapGasModal.tsx
import { jsx as jsx39 } from "react/jsx-runtime";
function SwapGasModal({ trigger, open, defaultOpen, onOpenChange }) {
  return /* @__PURE__ */ jsx39(Modal, { trigger, open, defaultOpen, onOpenChange, children: /* @__PURE__ */ jsx39(SwapGasView, {}) });
}

// src/components/switch-network-modal/SwitchNetworkModal.tsx
import { useEffect as useEffect10, useState as useState14 } from "react";
import { Fragment as Fragment2, jsx as jsx40 } from "react/jsx-runtime";
function SwitchNetworkModal() {
  const { wallet } = useCurrentWallet();
  const roochNetwork = useCurrentNetwork();
  const [open, setOpen] = useState14(false);
  const currentAddr = useCurrentAddress();
  const [targetNetwork, setTargetNetwork] = useState14();
  useEffect10(() => {
    if (!wallet) {
      return;
    }
    checkWalletNetwork(wallet, roochNetwork).then((r) => {
      setOpen(r !== void 0);
      setTargetNetwork(r);
    });
  }, [wallet, currentAddr, roochNetwork]);
  const switchNetwork = async (wallet2, target) => {
    return wallet2?.switchNetwork(target);
  };
  return wallet && /* @__PURE__ */ jsx40(Modal, { trigger: /* @__PURE__ */ jsx40(Fragment2, {}), open, defaultOpen: false, onOpenChange: (v) => setOpen(v), children: /* @__PURE__ */ jsx40(
    SwitchNetworkView,
    {
      wallet,
      onSuccess: () => {
      },
      targetNetWork: targetNetwork,
      switchNetwork
    }
  ) });
}

// src/components/DropdownMenu.tsx
import { Fragment as Fragment3, jsx as jsx41, jsxs as jsxs22 } from "react/jsx-runtime";
function ActionDropdownMenu() {
  const address = useCurrentAddress();
  const [sessionOpen, setSessionOpen] = useState15(false);
  const [faucetOpen, setFaucetOpen] = useState15(false);
  const [swapGasOpen, setSwapGasOpen] = useState15(false);
  const [isLoading, setIsLoading] = useState15(false);
  const [progress, setProgress] = useState15(0);
  const subscribeOnRequest = useSubscribeOnRequest();
  const subscribeOnError = useSubscribeOnError();
  const client = useRoochClient();
  const [rGasBalance, setRGasBalance] = useState15(0);
  const getBalance = useCallback5(() => {
    if (!address) {
      return;
    }
    client.getBalance({
      owner: address,
      coinType: "0x3::gas_coin::RGas"
    }).then((result) => {
      setRGasBalance(result.fixedBalance);
    });
  }, [address, client]);
  useEffect11(() => {
    getBalance();
  }, [getBalance]);
  useEffect11(() => {
    const unsubscribe = subscribeOnRequest((status) => {
      switch (status) {
        case "requesting":
          startProgress();
          break;
        case "error":
          setIsLoading(false);
          break;
        case "success":
          getBalance();
          setIsLoading(false);
          break;
      }
    });
    const UnsubscribeOnError = subscribeOnError((error) => {
      if (error.code === ErrorValidateCantPayGasDeposit) {
        setFaucetOpen(true);
      }
    });
    return () => {
      unsubscribe();
      UnsubscribeOnError();
    };
  }, [subscribeOnRequest, subscribeOnError, address, getBalance]);
  const startProgress = () => {
    setIsLoading(true);
    setProgress(0);
    const interval = setInterval(() => {
      setProgress((prev) => {
        const nextProgress = prev + 10;
        if (nextProgress >= 90) {
          clearInterval(interval);
        }
        return nextProgress;
      });
    }, 100);
  };
  return /* @__PURE__ */ jsxs22(Fragment3, { children: [
    /* @__PURE__ */ jsx41(SwitchNetworkModal, {}),
    /* @__PURE__ */ jsx41(SessionModal, { trigger: /* @__PURE__ */ jsx41(Fragment3, {}), open: sessionOpen, onOpenChange: (v) => setSessionOpen(v) }),
    /* @__PURE__ */ jsx41(
      FaucetModal,
      {
        trigger: /* @__PURE__ */ jsx41(Fragment3, {}),
        open: faucetOpen,
        onOpenChange: (v) => setFaucetOpen(v),
        swapRGas: () => {
          setFaucetOpen(false);
          setSwapGasOpen(true);
        }
      }
    ),
    /* @__PURE__ */ jsx41(SwapGasModal, { trigger: /* @__PURE__ */ jsx41(Fragment3, {}), open: swapGasOpen, onOpenChange: (v) => setSwapGasOpen(v) }),
    /* @__PURE__ */ jsxs22(DropdownMenu.Root, { modal: false, children: [
      /* @__PURE__ */ jsx41(StyleMarker, { children: /* @__PURE__ */ jsx41(DropdownMenu.Trigger, { asChild: true, children: /* @__PURE__ */ jsx41("div", { style: { position: "relative", display: "inline-block" }, children: /* @__PURE__ */ jsxs22(Button, { variant: "outline", className: connectedAddress, children: [
        /* @__PURE__ */ jsxs22("div", { className: addressContainer, children: [
          /* @__PURE__ */ jsx41(Text, { mono: true, children: address?.toShortStr() || "" }),
          /* @__PURE__ */ jsx41(Text, { className: rgasBalance, children: `RGAS: ${rGasBalance.toFixed(4)}` })
        ] }),
        /* @__PURE__ */ jsx41(ChevronIcon, {}),
        isLoading && /* @__PURE__ */ jsx41("div", { className: progressBar2, style: { width: `${progress}%` } })
      ] }) }) }) }),
      /* @__PURE__ */ jsx41(DropdownMenu.Portal, { children: /* @__PURE__ */ jsx41(StyleMarker, { className: menuContainer, children: /* @__PURE__ */ jsxs22(DropdownMenu.Content, { className: menuContent, children: [
        /* @__PURE__ */ jsx41(
          DropdownMenu.Item,
          {
            className: clsx10(menuItem, switchMenuItem),
            onSelect: () => {
              setFaucetOpen(true);
            },
            children: /* @__PURE__ */ jsx41(Text, { mono: true, children: "Faucet" })
          }
        ),
        /* @__PURE__ */ jsx41(
          DropdownMenu.Item,
          {
            className: clsx10(menuItem, switchMenuItem),
            onSelect: () => {
              setSwapGasOpen(true);
            },
            children: /* @__PURE__ */ jsx41(Text, { mono: true, children: "Swap RGas" })
          }
        ),
        /* @__PURE__ */ jsx41(
          DropdownMenu.Item,
          {
            className: clsx10(menuItem, switchMenuItem),
            onSelect: () => {
              setSessionOpen(true);
            },
            children: /* @__PURE__ */ jsx41(Text, { mono: true, children: "Sessions Manager" })
          }
        ),
        /* @__PURE__ */ jsx41(
          DropdownMenu.Item,
          {
            className: clsx10(menuItem, switchMenuItem),
            onSelect: () => {
              const prefix = "rooch-sdk-kit";
              Object.keys(localStorage).forEach((key) => {
                if (key.startsWith(prefix)) {
                  localStorage.removeItem(key);
                }
              });
              window.location.reload();
            },
            children: /* @__PURE__ */ jsx41(Text, { mono: true, children: "Disconnect" })
          }
        )
      ] }) }) })
    ] })
  ] });
}

// src/components/ConnectButton.tsx
import { jsx as jsx42 } from "react/jsx-runtime";
function ConnectButton({
  connectText = "Connect Wallet",
  ...buttonProps
}) {
  const address = useCurrentAddress();
  return address ? /* @__PURE__ */ jsx42(StyleMarker, { children: /* @__PURE__ */ jsx42(ActionDropdownMenu, {}) }) : /* @__PURE__ */ jsx42(
    ConnectModal,
    {
      trigger: /* @__PURE__ */ jsx42(StyleMarker, { children: /* @__PURE__ */ jsx42(Button, { variant: "outline", ...buttonProps, children: connectText }) })
    }
  );
}

// src/components/WalletGuard.tsx
import { Fragment as Fragment4, jsx as jsx43 } from "react/jsx-runtime";
function WalletGuard({ children, onClick }) {
  const { status } = useCurrentWallet();
  return /* @__PURE__ */ jsx43(Fragment4, { children: status === "connected" ? /* @__PURE__ */ jsx43(
    "button",
    {
      style: {
        all: "unset",
        cursor: "pointer"
      },
      onClick,
      children
    }
  ) : /* @__PURE__ */ jsx43(ConnectModal, { trigger: children, onSuccess: onClick }) });
}
export {
  AMOUNT_INVALID,
  All_NETWORK,
  BitcoinWallet,
  ConnectButton,
  ConnectModal,
  FaucetModal,
  NOT_SUPPORT_ADDRESS,
  OkxWallet,
  OnekeyWallet,
  RoochContext,
  RoochProvider,
  SessionKeyGuard,
  SessionModal,
  USER_REJECTED,
  UniSatWallet,
  Wallet,
  WalletContext,
  WalletError,
  WalletGuard,
  WalletProvider,
  createNetworkConfig,
  useAddresses,
  useAutoConnectWallet,
  useConnectWallet,
  useConnectionStatus,
  useCreateSessionKey,
  useCurrentAddress,
  useCurrentNetwork,
  useCurrentSession,
  useCurrentWallet,
  useRemoveSession,
  useRoochClient,
  useRoochClientQuery,
  useRoochContext,
  useSession,
  useSessions,
  useSignAndExecuteTransaction,
  useSubscribeOnError,
  useSubscribeOnRequest,
  useSwitchNetwork,
  useTransferCoin,
  useTransferObject,
  useWalletChanged,
  useWallets
};
//# sourceMappingURL=index.js.map
